/* Presentation only: keep the builder's original inputs, handlers and pricing. */
(() => {
  'use strict';
  const selector = '[data-swishcb-configurator="cards"]';
  const nativeSidebar = node => !!node?.closest('[data-swishcb-native-sidebar="true"]');
  const states = new WeakMap();
  const logoArtwork = new WeakMap();
  const text = (node, value) => { if (node && node.textContent !== value) node.textContent = value; };
  const contentOf = panel => panel?.querySelector(':scope > .swishcb__panel__content, :scope > .swishcb__panel-content');
  const append = (parent, child) => { if (parent && child && child.parentNode !== parent) parent.appendChild(child); };
  const make = (tag, className, value) => {
    const node = document.createElement(tag);
    node.className = className;
    if (value) node.textContent = value;
    return node;
  };
  const selectedText = node => node?.selectedOptions?.[0]?.textContent || '';

  function checkboxDropdowns(root) {
    root.querySelectorAll('[data-swishcb-checkbox-dropdown]').forEach(dropdown => {
      const choices = Array.from(dropdown.querySelectorAll('input[type="checkbox"]')).filter(input => {
        const label = input.closest('label');
        return label && !label.hidden && label.getAttribute('aria-hidden') !== 'true' && label.style.display !== 'none';
      });
      const selected = choices.filter(input => input.checked);
      const value = selected.length && selected.length === choices.length ? 'All'
        : selected.map(input => input.closest('label').textContent.trim()).join(', ') || 'None';
      text(dropdown.querySelector('[data-swishcb-checkbox-selection]'), value);
    });
  }

  function setExpanded(panel, expanded) {
    if (!panel) return;
    panel.open = true;
    panel.dataset.swishcbSummaryCollapsed = String(!expanded);
    if (panel.dataset.swishcbSection !== 'true') panel.querySelector(':scope > summary')?.setAttribute('aria-expanded', String(expanded));
  }
  function openPanel(panel) {
    if (!panel) return;
    setExpanded(panel, true);
    const parent = panel.parentElement?.closest('details[data-swishcb-panel]');
    if (parent) openPanel(parent);
  }
  function section(panel, heading, description) {
    if (!panel) return;
    const summary = panel.querySelector(':scope > summary');
    if (!summary) return;
    panel.dataset.swishcbNativeDisclosure = 'true';
    panel.dataset.swishcbStaticOpen = 'true';
    panel.dataset.swishcbSection = 'true';
    // Retain the legacy details node for the builder, with a regular visible heading.
    // Its hidden summary is no longer an accordion control or a keyboard stop.
    summary.hidden = true;
    summary.setAttribute('aria-hidden', 'true');
    summary.setAttribute('tabindex', '-1');
    summary.removeAttribute('role');
    summary.removeAttribute('aria-expanded');
    summary.removeAttribute('aria-controls');
    let header = panel.querySelector(':scope > .swishcb__section-heading');
    if (!header) {
      header = make('div', 'swishcb__section-heading');
      const title = summary.querySelector('.swishcb__panel-summary-title');
      if (title) header.appendChild(title);
      summary.after(header);
    }
    // Native sidebar headings are rendered with their final text in Liquid.
    if (!nativeSidebar(panel)) text(header.querySelector('.swishcb__panel-title'), heading);
    let meta = header.querySelector('.swishcb__nav-description');
    if (!meta) {
      meta = make('span', 'swishcb__nav-description');
      (header.querySelector('.swishcb__panel-summary-title') || header).appendChild(meta);
    }
    if (!nativeSidebar(panel) || !meta.textContent.trim()) text(meta, description);
    panel.querySelectorAll('.swishcb__nav-number,.swishcb__nav-chevron').forEach(node => node.remove());
    if (!panel.dataset.swishcbNavigationBound) {
      panel.dataset.swishcbNavigationBound = 'true';
      summary.addEventListener('click', event => event.preventDefault());
      panel.addEventListener('toggle', () => { if (!panel.open) panel.open = true; });
    }
    setExpanded(panel, true);
  }

  function artworkControls(root) {
    const panel = root.querySelector('[data-swishcb-panel="logos"]');
    const customize = root.querySelector('#swishcb-color-customize-panel');
    if (!panel || !customize) return;
    // Keep the original artwork managers and their handlers in the colors card.
    panel.classList.add('swishcb__artwork-controls');
    panel.removeAttribute('data-swishcb-section');
    panel.removeAttribute('data-swishcb-configurator-card');
    panel.dataset.swishcbNativeDisclosure = 'true';
    panel.dataset.swishcbStaticOpen = 'true';
    panel.dataset.swishcbSummaryCollapsed = 'false';
    panel.open = true;
    const summary = panel.querySelector(':scope > summary');
    if (summary) {
      summary.hidden = true;
      summary.setAttribute('aria-hidden', 'true');
      summary.setAttribute('tabindex', '-1');
    }
    panel.querySelectorAll(':scope > .swishcb__section-heading,:scope > .swishcb__logo-guide,:scope > .swishcb__logo-guide-status').forEach(node => node.remove());
    // Native Liquid already places this node correctly; only adopt older markup.
    if (customize.nextElementSibling !== panel) customize.after(panel);
  }

  function updateLogoIllustration(root, logo) {
    if (!root?.matches(selector)) return;
    const artwork = logoArtwork.get(root) || {};
    artwork.logo = logo || null;
    logoArtwork.set(root, artwork);
    const guide = root.querySelector('.swishcb__logo-guide');
    if (!guide) return;
    const logoId = logo?.id || '';
    if (guide.dataset.logoId !== logoId) guide.dataset.logoId = logoId;
    paintLogoDiagram(root);
  }

  function logoDiagramTarget(snapshot) {
    const center = {x: .5, y: .5, width: .45, height: .35, rotation: 0, side: 'center', fill: snapshot.mainColor};
    if (snapshot.hoopSides?.length !== 1) return center;
    const key = snapshot.logoKeyRects?.find(rect => rect.side === snapshot.hoopSides[0]);
    const r = key?.pixelBounds, surface = snapshot.surface;
    if (!r || !surface || !(surface.w > 0 && surface.h > 0)) return center;
    const x0 = Math.max(0, (r.x - surface.x) / surface.w);
    const y0 = Math.max(0, (r.y - surface.y) / surface.h);
    const x1 = Math.min(1, (r.x + r.w - surface.x) / surface.w);
    const y1 = Math.min(1, (r.y + r.h - surface.y) / surface.h);
    if (!(x1 > x0 && y1 > y0)) return center;
    return {x: (x0+x1)/2, y: (y0+y1)/2, width: x1-x0, height: y1-y0,
      rotation: {left: -90, right: 90, top: 0, bottom: 180}[key.side], side: key.side, fill: key.fill};
  }

  function paintLogoDiagram(root) {
    const state = states.get(root), data = state?.courtSnapshot;
    const guide = root.querySelector('.swishcb__logo-guide');
    const bitmap = state?.logoCourtBitmap || state?.courtBitmap;
    if (!data || !bitmap || !guide) return;
    const f = courtDiagramLayout(data.lengthM, data.widthM, data.rotationDeg, false);
    const canvas = guide.querySelector('canvas');
    canvas.width = Math.ceil(f.width * 2);
    canvas.height = Math.ceil(f.height * 2);
    const ctx = canvas.getContext('2d');
    ctx.setTransform(2, 0, 0, 2, 0, 0);
    ctx.translate(f.x, f.y);
    ctx.rotate(f.angle);
    ctx.drawImage(bitmap, -f.w/2, -f.h/2, f.w, f.h);
    const target = data.logoTarget;
    const placeholder = !logoArtwork.get(root)?.logo?.source;
    if (placeholder) {
      const angle = target.rotation * Math.PI / 180;
      const across = Math.abs(Math.cos(angle)) * target.width * f.w + Math.abs(Math.sin(angle)) * target.height * f.h;
      const depth = Math.abs(Math.sin(angle)) * target.width * f.w + Math.abs(Math.cos(angle)) * target.height * f.h;
      ctx.translate((target.x-.5)*f.w, (target.y-.5)*f.h);
      ctx.rotate(angle);
      const size = Math.min(20, depth * (target.side === 'center' ? .5 : .25), across * .8 / 5.2);
      ctx.font = `700 ${size}px "Poppins", sans-serif`;
      const measured = ctx.measureText('Your Logo').width;
      const fontSize = measured > across * .8 ? size * across * .8 / measured : size;
      ctx.font = `700 ${fontSize}px "Poppins", sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'alphabetic';
      const ink = ctx.measureText('Your Logo');
      const textX = Number.isFinite(ink.actualBoundingBoxLeft + ink.actualBoundingBoxRight)
        ? (ink.actualBoundingBoxLeft - ink.actualBoundingBoxRight) / 2 : 0;
      const textY = Number.isFinite(ink.actualBoundingBoxAscent + ink.actualBoundingBoxDescent)
        ? (ink.actualBoundingBoxAscent - ink.actualBoundingBoxDescent) / 2 : fontSize * .35;
      // Sample around the text center so a thin center line cannot choose
      // the contrast for the whole label. Tainted artwork uses the fill.
      let rgb = /^#([\da-f]{6})$/i.exec(target.fill || data.mainColor || '#121212')?.[1] || '121212';
      let channels = [0,2,4].map(i => parseInt(rgb.slice(i,i+2),16));
      let luminance = channels[0]*.2126 + channels[1]*.7152 + channels[2]*.0722;
      try {
        const samples = [], bitmapCtx = bitmap.getContext('2d');
        for (const dx of [-.025, 0, .025]) for (const dy of [-.025, 0, .025]) {
          const pixel = bitmapCtx.getImageData(
            Math.min(bitmap.width-1, Math.max(0, Math.round((target.x+dx)*bitmap.width))),
            Math.min(bitmap.height-1, Math.max(0, Math.round((target.y+dy)*bitmap.height))), 1, 1).data;
          if (pixel[3] > 0) samples.push(pixel[0]*.2126 + pixel[1]*.7152 + pixel[2]*.0722);
        }
        if (samples.length) luminance = samples.sort((a,b) => a-b)[Math.floor(samples.length/2)];
      } catch (_) {}
      const light = luminance > 150;
      ctx.fillStyle = light ? '#121212' : '#FFFFFF';
      ctx.strokeStyle = light ? '#FFFFFF' : '#121212';
      ctx.lineWidth = Math.max(.25, fontSize * .035);
      ctx.lineJoin = 'round';
      ctx.strokeText('Your Logo',textX,textY);
      ctx.fillText('Your Logo',textX,textY);
      guide.dataset.logoFontSize = String(fontSize);
    }
    guide.dataset.logoPlaceholder = String(placeholder);
    guide.dataset.logoSide = target.side;
    guide.dataset.logoX = String(target.x);
    guide.dataset.logoY = String(target.y);
    guide.dataset.logoRotation = String(data.rotationDeg + target.rotation);
    guide.dataset.courtType = data.ruleset;
    guide.dataset.courtHoops = String(data.hoopCount);
    guide.dataset.courtWidth = String(data.widthM);
    guide.dataset.courtLength = String(data.lengthM);
    guide.dataset.courtView = data.view;
    guide.dataset.courtReady = 'true';
  }

  function contactSection(panel) {
    if (!panel) return;
    const summary = panel.querySelector(':scope > summary');
    if (!summary) return;
    panel.dataset.swishcbNativeDisclosure = 'true';
    panel.dataset.swishcbStaticOpen = 'true';
    const root = panel.closest(selector);
    const contactOnly = !surfaceSupportsCheckout(root);
    let header = panel.querySelector(':scope > .swishcb__contact-heading');
    const title = (header || summary).querySelector('.swishcb__panel-summary-title');
    if (!header) {
      header = make('div', 'swishcb__contact-heading');
      summary.after(header);
    }
    append(header, title);
    summary.hidden = true;
    summary.setAttribute('aria-hidden', 'true');
    summary.setAttribute('tabindex', '-1');
    summary.removeAttribute('aria-expanded');
    text(title.querySelector('.swishcb__panel-title'), customSurfaceRequiresQuote(root) ? 'Get Pricing' : (contactOnly ? 'Contact Us' : 'Need Help?'));
    let meta = title.querySelector('.swishcb__nav-description');
    if (!meta) {
      meta = make('span', 'swishcb__nav-description');
      title.appendChild(meta);
    }
    text(meta, 'Fill out the contact form, and our team will respond within 24 hours to help you set up your court at no additional cost.');
    if (!panel.dataset.swishcbNavigationBound) {
      panel.dataset.swishcbNavigationBound = 'true';
      const content = contentOf(panel);
      if (content) {
        if (!content.id) content.id = `${panel.closest('.swishcb')?.id || 'swishcb'}-navigation-contact`;
        summary.setAttribute('aria-controls', content.id);
      }
      panel.addEventListener('toggle', () => { if (!panel.open) panel.open = true; });
    }
    setExpanded(panel, true);
    summary.removeAttribute('aria-expanded');
  }

  function palettes(root) {
    window.SwishCBColorPresetSwatches?.sync(root);
    const preset = root.querySelector('#color-preset');
    if (preset) {
      preset.setAttribute('aria-label', 'Color preset');
      preset.closest('.swishcb__row')?.setAttribute('data-swishcb-preset-row', '');
    }
    if (preset && preset.parentElement.firstElementChild !== preset) preset.parentElement.prepend(preset);
    root.querySelectorAll('[data-swishcb-panel="colors"] .swishcb__color-row').forEach(row => {
      if (row.closest('#swishcb-more-options-panel')) return;
      const input = row.querySelector('[data-color-custom-input]');
      const custom = row.querySelector('[data-color-custom-row]');
      const choices = row.querySelector(':scope > .swishcb__color-choices');
      if (!input || !custom || !choices) return;
      row.dataset.swishcbPaletteInline = 'true';
      delete row.dataset.swishcbPaletteOpen;
      row.querySelector('.swishcb__palette-toggle')?.remove();
      let heading = row.querySelector(':scope > .swishcb__palette-heading');
      const labelNode = row.querySelector('label');
      if (!heading) {
        heading = make('div', 'swishcb__palette-heading');
        row.prepend(heading);
      }
      let title = heading.querySelector('.swishcb__palette-title');
      if (!title) {
        title = make('div', 'swishcb__palette-title');
        heading.prepend(title);
      }
      // Move the bound Custom control with its inputs beside the label, outside
      // the swatch host that the color renderer replaces on color changes.
      append(title, labelNode);
      append(heading, custom);
      let selectedColor = title.querySelector('.swishcb__selected-color');
      const label = labelNode?.textContent.trim() || 'Court';
      choices.setAttribute('role', 'group');
      choices.setAttribute('aria-label', `${label} colors`);
      const customize = custom.querySelector('[data-color-customize]');
      const swatches = [...choices.querySelectorAll('[data-hex]')];
      row.dataset.swishcbSwatchCount = String(swatches.length);
      const state = states.get(root);
      if (!state.paletteObserver) {
        state.paletteObserver = new ResizeObserver(entries => entries.forEach(({target, contentRect}) => sizePalette(target, contentRect.width)));
      }
      if (!row.dataset.swishcbPaletteObserved) {
        row.dataset.swishcbPaletteObserved = 'true';
        state.paletteObserver.observe(row);
      }
      const swatch = swatches.find(s => s.dataset.hex.toLowerCase() === input.value.toLowerCase());
      const color = swatch?.getAttribute('title') || input.value.toUpperCase();
      if (labelNode) {
        if (!selectedColor) {
          selectedColor = make('button', 'swishcb__selected-color');
          selectedColor.type = 'button';
          title.append(' - ', selectedColor);
        }
        text(selectedColor, color);
        selectedColor.setAttribute('aria-label', `Change ${label.toLowerCase()} color: ${color}`);
        selectedColor.setAttribute('aria-haspopup', 'dialog');
      }
      if (customize) {
        text(customize, 'Custom');
        customize.setAttribute('aria-label', `Custom ${label.toLowerCase()} color`);
        customize.setAttribute('aria-haspopup', 'dialog');
        customize.setAttribute('title', `Current color: ${color} · ${input.value.toUpperCase()}`);
        customize.dataset.swishcbCustomSelected = String(!swatch);
        customize.tabIndex = 0;
      }
      // Custom is a separate heading action. Keep one Tab stop in the swatch grid.
      const buttons = swatches;
      const current = buttons.includes(document.activeElement) ? document.activeElement : swatch || buttons[0];
      buttons.forEach(button => { button.tabIndex = button === current ? 0 : -1; });
      sizePalette(row, row.clientWidth);
    });
  }

  function sizePalette(row, width) {
    if (!width) return;
    const count = Number(row.dataset.swishcbSwatchCount);
    const minimum = matchMedia('(any-pointer: coarse)').matches ? 44 : 32;
    const wideColumns = Math.ceil(count / 3);
    const columns = Math.max(2, Math.min(wideColumns, Math.floor((width + 2) / (minimum + 2))));
    if (row.style.getPropertyValue('--swishcb-palette-columns') !== String(columns)) {
      row.style.setProperty('--swishcb-palette-columns', columns);
    }
  }

  function paletteKeydown(event) {
    const row = event.target.closest('[data-swishcb-palette-inline]');
    if (!row || !event.target.matches('button')) return;
    const buttons = [...row.querySelectorAll('button[data-hex]')];
    const index = buttons.indexOf(event.target);
    if (index < 0) return;
    const rect = event.target.getBoundingClientRect();
    let next;
    const rtl = getComputedStyle(row).direction === 'rtl';
    if (event.key === 'ArrowRight') next = (index + (rtl ? -1 : 1) + buttons.length) % buttons.length;
    if (event.key === 'ArrowLeft') next = (index + (rtl ? 1 : -1) + buttons.length) % buttons.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = buttons.length - 1;
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      const down = event.key === 'ArrowDown';
      const candidates = buttons.map((button, i) => ({i, r: button.getBoundingClientRect()}))
        .filter(({r}) => down ? r.top > rect.top + 1 : r.top < rect.top - 1);
      candidates.sort((a, b) => Math.abs(a.r.top - rect.top) - Math.abs(b.r.top - rect.top) ||
        Math.abs(a.r.left + a.r.width / 2 - rect.left - rect.width / 2) - Math.abs(b.r.left + b.r.width / 2 - rect.left - rect.width / 2));
      next = candidates[0]?.i ?? index;
    }
    if (next === undefined) return;
    event.preventDefault();
    event.stopPropagation();
    buttons.forEach((button, i) => { button.tabIndex = i === next ? 0 : -1; });
    buttons[next].focus({preventScroll: true});
    const target = buttons[next].getBoundingClientRect();
    const root = row.closest(selector);
    const bottom = innerHeight - (parseFloat(root.style.getPropertyValue('--swishcb-bottom-bar-height')) || 0);
    const top = Math.max(80, (parseFloat(root.style.getPropertyValue('--swishcb-sticky-offset')) || 0) + (parseFloat(root.style.getPropertyValue('--swishcb-pinned-preview-height')) || 0));
    if (target.bottom > bottom || target.top < top) buttons[next].scrollIntoView({block: 'center', behavior: 'instant'});
  }

  function navigationLabels(root) {
    const names = {'Trending':'Color presets', 'Main Court Width':'Playing area width', 'Main Court Length':'Playing area length',
      'Edges':'Edges', 'Line Fit':'Line fit', 'Key Width':'Key width', 'Key Length':'Key length', '3-Point Distance':'3-point distance',
      'Center Circle Diameter':'Center circle diameter', '3D Hoop Model':'3D hoop model', 'Popular Sizes':'Popular sizes',
      'Font Size':'Font size', 'Font Weight':'Font weight', 'Font Color':'Font color', 'Stroke Color':'Stroke color', 'Stroke Size':'Stroke size'};
    root.querySelectorAll('#swishcb-sidebar-controls .swishcb__tt--label').forEach(label => {
      const name = names[label.textContent.trim()];
      if (name && !(nativeSidebar(root) && label.closest('[data-swishcb-native-row="true"]'))) {
        text(label, name);
        label.setAttribute('aria-label', `${name} information`);
      }
    });
    // Tooltip triggers have their own names; they must not become the input name.
    root.querySelectorAll('#swishcb-sidebar-controls label').forEach(label => {
      const input = label.control;
      if (!input || !label.querySelector('[data-swishcb-tooltip]')) return;
      const name = [...label.childNodes].filter(node => !node.classList?.contains('swishcb__selected-color'))
        .map(node => node.textContent).join('').trim();
      if (name && input.getAttribute('aria-label') !== name) input.setAttribute('aria-label', name);
    });
  }

  function contactDimensions(root) {
    const width = root.querySelector('#swishcb-contact-width');
    const form = width?.closest('form');
    if (!form) return;
    // Do not interrupt a partially typed measurement or its existing debounce.
    const active = document.activeElement;
    const units = root.querySelector('#swishcb-units-select')?.value || 'm';
    ['width', 'length'].forEach(axis => {
      const value = root.querySelector(`#swishcb-${axis}`)?.value;
      const field = form.querySelector(`#swishcb-contact-${axis}`);
      if (field && field !== active && value && Number(value) > 0 && field.value !== value) field.value = value;
    });
    form.querySelectorAll('input[name="swishcb-contact-units"]').forEach(radio => { radio.checked = radio.value === units; });
    form.querySelectorAll('[data-swishcb-contact-unit-select]').forEach(select => { select.value = units; });
    form.dataset.swishcbContactUnits = units;
  }

  function closeActions(root) {
    const pairs = [
      ['#swishcb-main-options-panel', '#swishcb-main-options-toggle', 'Close more options'],
      ['#swishcb-color-customize-panel', '#swishcb-color-customize-toggle', 'Close customize options'],
      ['#swishcb-more-options-panel', '#swishcb-more-options-toggle', 'Close advanced options'],
      ['#swishcb-add-more-sports-panel', '[data-action="toggle-add-more-sports"]', 'Close sports options'],
      ['[data-media-manager-panel="text"]', '[data-media-manager-toggle="text"]', 'Close text settings']
    ];
    pairs.forEach(([panelSelector, toggleSelector, label]) => {
      const panel = root.querySelector(panelSelector), toggle = root.querySelector(toggleSelector);
      if (!panel || !toggle) return;
      let close = panel.querySelector(':scope > [data-swishcb-close-options]');
      if (!close) {
        close = make('button', 'swishcb__close-options', label);
        close.type = 'button';
        close.dataset.swishcbCloseOptions = '';
      }
      if (!close.dataset.swishcbCloseBound) {
        close.dataset.swishcbCloseBound = 'true';
        close.addEventListener('click', () => {
          const manager = panel.closest('[data-media-manager]');
          if (!manager) toggle.click();
          else {
            manager.open = false;
            toggle.setAttribute('aria-expanded', 'false');
          }
          requestAnimationFrame(() => {
            toggle.focus({preventScroll: true});
            toggle.scrollIntoView({block: 'center', behavior: 'instant'});
          });
        });
      }
      if (panel.lastElementChild !== close) panel.append(close);
    });
  }

  function focusMeasurementField(input) {
    if (!input || !input.getClientRects().length) return;
    input.focus({preventScroll: true});
    input.select();
    // Mobile fields share the keyboard-aware controller; row scrolling would
    // otherwise compete with it while Safari is opening its numeric keyboard.
    if (states.get(input.closest(selector))?.editorFocus?.reveal(input)) return;
    const reveal = () => {
      if (document.activeElement === input) {
        (input.closest('.swishcb__row') || input).scrollIntoView({block: 'center', inline: 'nearest', behavior: 'instant'});
      }
    };
    reveal();
    requestAnimationFrame(reveal);
  }

  function mobileEditorFocus(root, controls, state) {
    const stacked = matchMedia('(max-width: 1199px), (max-width: 1366px) and (any-pointer: coarse)');
    const viewport = window.visualViewport;
    const options = {signal: state.lifecycle.signal, passive: true};
    const editable = 'input:is([type="number"],[type="text"],[type="tel"],[type="email"],[type="url"],[type="search"],[type="password"],:not([type])),textarea';
    const capProperty = '--swishcb-keyboard-preview-limit';
    const shellProperties = ['--swishcb-keyboard-preview-top','--swishcb-keyboard-preview-left','--swishcb-keyboard-preview-width','--swishcb-keyboard-shell-height',capProperty,'--swishcb-keyboard-content-gutter-start','--swishcb-keyboard-content-gutter-end'];
    const minimumCanvasHeight = () => state.previewMenu?.minimumCanvasHeight || 56;
    let input = null, session = null, frame = 0, disposed = false;
    let closedPreview = null;
    let fullHeight = viewport?.height || window.innerHeight;
    let viewportWidth = window.innerWidth;
    const visibleViewport = () => ({top: Math.max(0, viewport?.offsetTop || 0), height: viewport?.height || window.innerHeight, scale: viewport?.scale || 1});
    const product = () => root.querySelector(':scope > .product');
    const media = () => product()?.querySelector(':scope > .product__media-wrapper');
    const information = () => product()?.querySelector(':scope > .product__info-wrapper');
    const isEditable = node => !!node?.matches?.(editable) && controls.contains(node) && !node.disabled && !node.readOnly && !node.closest('dialog,[role="dialog"]');
    const rendered = () => root.isConnected && input?.isConnected && input.getClientRects().length && input.getBoundingClientRect().height > 0;
    const enabled = () => !disposed && stacked.matches && !!media()?.querySelector('#swishcb-preview-controls');
    const touchKeyboardDevice = () => matchMedia('(any-pointer: coarse)').matches || (window.navigator?.maxTouchPoints || 0)>0;
    const threshold = () => Math.max(80, Math.min(180, fullHeight * .2));
    const setStyle = (name, value) => { if (root.style.getPropertyValue(name) !== value) root.style.setProperty(name, value); };
    const saveStyles = (node, names) => names.map(name => [name, node.style.getPropertyValue(name), node.style.getPropertyPriority(name)]);
    const restoreStyles = (node, values) => values.forEach(([name,value,priority]) => value ? node.style.setProperty(name,value,priority) : node.style.removeProperty(name));
    const focused = () => rendered() && document.activeElement === input && isEditable(input);
    function rememberClosedPreview() {
      if (session) return;
      const canvas=media()?.querySelector('.swishcb__viewport:not(.swishcb__viewport--hidden)');
      if (canvas) closedPreview={width:window.innerWidth,height:canvas.getBoundingClientRect().height};
    }

    function cancelTimer(name) {
      if (!session) return;
      if (session[name]) window.clearTimeout(session[name]);
      session[name] = 0;
    }
    function shellGeometry(view, updateTop = true) {
      if (!session) return;
      if (updateTop) setStyle(shellProperties[0], `${view.top}px`);
      setStyle(shellProperties[3], `${Math.max(1, view.height)}px`);
    }
    function fieldGap(view, toolbarHeight) {
      const height = rendered() ? Math.min(input.getBoundingClientRect().height, Math.max(44, view.height * .3)) : 44;
      return Math.min(30, Math.max(0, (view.height - toolbarHeight - height - minimumCanvasHeight()) / 2));
    }
    function fitOpening(view, smallerKeyboard = false) {
      if (!session || (session.phase === 'active' && !smallerKeyboard) || session.phase === 'restoring') return;
      // Opening can only shrink. Native keyboard animation must never alternate
      // larger/smaller courts; once settled, field changes and swipes keep it.
      if (view.height < 320) root.setAttribute('data-swishcb-keyboard-compact', '');
      const stageStyle = getComputedStyle(session.preview.querySelector('.swishcb__stage'));
      const stagePadding = (parseFloat(stageStyle.paddingTop) || 0) + (parseFloat(stageStyle.paddingBottom) || 0);
      const toolbarHeight = (state.previewMenu?.active ? (state.previewMenu.keyboardInset || 0) : session.toolbar.getBoundingClientRect().height) + stagePadding + 1;
      const fieldHeight = rendered() ? Math.min(input.getBoundingClientRect().height, Math.max(44, view.height * .3)) : 44;
      session.gap = fieldGap(view, toolbarHeight);
      setStyle('--swishcb-keyboard-field-gap', `${session.gap}px`);
      // A landscape keyboard can leave less space than the usual canvas
      // minimum. Keep a whole editable field visible in that smaller shell.
      const available = Math.max(1,view.height-toolbarHeight-fieldHeight);
      const minimum = Math.min(minimumCanvasHeight(),session.normalCanvasHeight,available);
      const limit = Math.max(minimum,Math.floor(Math.min(session.normalCanvasHeight,available-2*session.gap)));
      session.canvasLimit = Math.min(session.canvasLimit, limit);
      session.fitViewportHeight = Math.min(session.fitViewportHeight, view.height);
      setStyle(capProperty, `${session.canvasLimit}px`);
    }
    function revealInput() {
      if (!session || !focused() || session.manual || session.phase === 'restoring') return;
      const box = session.info.getBoundingClientRect();
      const field = input.getBoundingClientRect();
      const top = box.top + session.gap;
      const bottom = box.bottom - session.gap;
      const delta = field.height > bottom - top || field.top < top
        ? field.top - top : Math.max(0, field.bottom - bottom);
      // The finite settings scrollport is the sole focus-scroll owner. Never
      // drive window.scrollBy while Safari also pans its keyboard viewport.
      if (Math.abs(delta) > 1) session.info.scrollTop += delta;
    }
    function begin(view, keyboardAlreadyOpen = false) {
      if (session || !enabled() || !rendered() || view.scale > 1.02) return;
      const host = product(), preview = media(), info = information();
      const toolbar = preview?.querySelector('#swishcb-preview-controls');
      const canvas = preview?.querySelector('.swishcb__viewport:not(.swishcb__viewport--hidden)');
      if (!host || !preview || !info || !toolbar || !canvas) return;
      const box = host.getBoundingClientRect();
      const previewBox = preview.getBoundingClientRect();
      const content = info.querySelector('.product__info-container') || info;
      const beforeContentTop = content.getBoundingClientRect().top;
      const initialField = input.getBoundingClientRect();
      const initialFieldVisible = initialField.top >= view.top && initialField.bottom <= view.top + view.height;
      const savedX = window.scrollX || 0, savedY = window.scrollY || 0;
      const spacer = document.createElement('div');
      spacer.className = 'swishcb__keyboard-shell-spacer';
      spacer.setAttribute('aria-hidden', 'true');
      spacer.style.height = `${box.height}px`;
      const normalCanvasHeight = closedPreview?.width===window.innerWidth ? closedPreview.height : canvas.getBoundingClientRect().height;
      session = {host, preview, info, toolbar, content, spacer, savedX, savedY, savedInfoScroll:info.scrollTop,
        normalCanvasHeight, canvasLimit:normalCanvasHeight, fitViewportHeight:Infinity, gap:30, phase:keyboardAlreadyOpen?'opening':'preparing', manual:false,initialFieldVisible,
        openingTimer:0, recoveryTimer:0, prepareTimer:0, restoreFrame:0, touching:false,
        bodyStyles:saveStyles(document.body,['position','top','left','right','width','overflow','overscroll-behavior']),
        htmlStyles:saveStyles(document.documentElement,['overflow','overscroll-behavior','scroll-behavior'])};
      host.before(spacer);
      root.setAttribute('data-swishcb-editor-focusing', '');
      setStyle(shellProperties[1], `${previewBox.left}px`);
      setStyle(shellProperties[2], `${previewBox.width}px`);
      setStyle('--swishcb-keyboard-content-gutter-start',`${Math.max(0,box.left-previewBox.left)}px`);
      setStyle('--swishcb-keyboard-content-gutter-end',`${Math.max(0,previewBox.right-box.right)}px`);
      setStyle(capProperty, `${normalCanvasHeight}px`);
      setStyle('--swishcb-keyboard-field-gap', '30px');
      shellGeometry(view);
      // Establish the internal scrollport synchronously on touch focus, before
      // the browser starts its native keyboard reveal. Live nodes stay put.
      root.setAttribute('data-swishcb-keyboard-open', '');
      document.documentElement.style.setProperty('overflow','hidden','important');
      document.documentElement.style.setProperty('overscroll-behavior','none','important');
      document.documentElement.style.setProperty('scroll-behavior','auto','important');
      document.body.style.setProperty('position','fixed','important');
      document.body.style.setProperty('top',`${-savedY}px`,'important');
      document.body.style.setProperty('left',`${-savedX}px`,'important');
      document.body.style.setProperty('right','0','important');
      document.body.style.setProperty('width','100%','important');
      document.body.style.setProperty('overflow','hidden','important');
      document.body.style.setProperty('overscroll-behavior','none','important');
      // Transfer the visible settings location from page scrolling to the
      // existing wrapper, without moving or cloning a focused form control.
      info.scrollTop += content.getBoundingClientRect().top - beforeContentTop;
      revealInput();
      if (keyboardAlreadyOpen) fitOpening(view);
      else {
        session.prepareTimer = window.setTimeout(() => {
          if (session?.phase === 'preparing') finish(false,session.manual || !session.initialFieldVisible);
        }, 800);
      }
    }
    function finish(refresh = false, preserveSettings = true) {
      if (!session) return;
      const ending = session;
      cancelTimer('openingTimer');cancelTimer('recoveryTimer');cancelTimer('prepareTimer');
      cancelAnimationFrame(ending.restoreFrame);
      cancelAnimationFrame(frame);frame = 0;
      const contentTop = ending.content.getBoundingClientRect().top;
      session = null;
      root.removeAttribute('data-swishcb-keyboard-restoring');
      root.removeAttribute('data-swishcb-keyboard-open');
      root.removeAttribute('data-swishcb-keyboard-compact');
      shellProperties.forEach(name => root.style.removeProperty(name));
      root.style.removeProperty('--swishcb-keyboard-field-gap');
      ending.info.scrollTop = ending.savedInfoScroll;
      ending.spacer.remove();
      restoreStyles(document.body,ending.bodyStyles);
      restoreStyles(document.documentElement,ending.htmlStyles);
      setStyle('--swishcb-preview-mobile-width',`${document.documentElement.clientWidth}px`);
      // Preserve the setting the customer scrolled to, not the page position
      // from before they explored the bounded keyboard navigation.
      const desiredY = preserveSettings
        ? ending.content.getBoundingClientRect().top + (window.scrollY || 0) - contentTop
        : ending.savedY;
      const maxY = Math.max(0,document.documentElement.scrollHeight - window.innerHeight);
      window.scrollTo({left:ending.savedX,top:Math.max(0,Math.min(maxY,desiredY)),behavior:'instant'});
      root.removeAttribute('data-swishcb-editor-focusing');
      root.removeAttribute('data-swishcb-keyboard-preview-unpinned');
      rememberClosedPreview();
      if (refresh) window.dispatchEvent(new Event('resize'));
    }
    function restore() {
      if (!session || session.phase === 'restoring') return;
      state.previewMenu?.close();
      cancelTimer('openingTimer');cancelTimer('prepareTimer');cancelTimer('recoveryTimer');
      session.phase = 'restoring';
      root.setAttribute('data-swishcb-keyboard-restoring', '');
      session.toolbar.getBoundingClientRect();
      root.removeAttribute('data-swishcb-keyboard-compact');
      // A dimension edit can change the normal preview's intrinsic height.
      // Resolve its latest CSS-clamped height while the keyboard cap still
      // owns the visible canvas, then restore directly to that final size.
      root.dispatchEvent(new Event('swishcb:responsive-preview-refresh'));
      const naturalHeight = Number(root.dataset.swishcbResponsivePreviewNaturalHeight);
      if (Number.isFinite(naturalHeight) && naturalHeight > 0) {
        root.style.setProperty('--swishcb-responsive-preview-height',`${naturalHeight}px`);
        const probe = document.createElement('div');
        probe.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;width:0;height:var(--swishcb-mobile-preview-height);min-height:0;max-height:none;contain:strict;';
        session.preview.appendChild(probe);
        const targetHeight = probe.getBoundingClientRect().height;
        probe.remove();
        if (targetHeight > 0) session.normalCanvasHeight = targetHeight;
      }
      const from = session.canvasLimit, to = session.normalCanvasHeight;
      if (matchMedia('(prefers-reduced-motion: reduce)').matches || Math.abs(to-from) <= 1) { finish(true); return; }
      const active = session, started = window.performance.now();
      const tick = () => {
        if (session !== active || active.phase !== 'restoring') return;
        const progress = Math.min(1,(window.performance.now()-started)/160);
        const eased = 1-(1-progress)**3;
        setStyle(capProperty,`${from+(to-from)*eased}px`);
        if (progress < 1) active.restoreFrame=requestAnimationFrame(tick);
        else finish(true);
      };
      active.restoreFrame=requestAnimationFrame(tick);
    }
    function position() {
      frame=0;
      if (!enabled()) { finish(false); return; }
      const view=visibleViewport();
      if (view.scale>1.02) { finish(false); return; }
      const heightLoss=fullHeight-view.height;
      if (!session) {
        const nativeKeyboardViewport=touchKeyboardDevice() || (viewport && view.height<window.innerHeight-80);
        if (focused() && nativeKeyboardViewport && heightLoss>threshold()+12) begin(view,true);
        else { if (!focused() || !nativeKeyboardViewport) {fullHeight=view.height;rememberClosedPreview();} return; }
      }
      if (!session) return;
      if (session.phase==='restoring') {
        if (heightLoss<=threshold()+12) return;
        cancelAnimationFrame(session.restoreFrame);
        root.removeAttribute('data-swishcb-keyboard-restoring');
        // A new keyboard uses the original session cap, never an intermediate
        // restoration height. Keep the same document lock and scrolling row.
        setStyle(capProperty,`${session.canvasLimit}px`);
        session.phase='active';
      }
      if (heightLoss>threshold()+12) {
        cancelTimer('prepareTimer');cancelTimer('recoveryTimer');
        if (session.phase==='preparing') session.phase='opening';
        if (session.phase==='active' && view.height<session.fitViewportHeight-1) {
          // A text/language keyboard can be taller than the numeric keyboard.
          // Shrink only for genuinely less room; never grow on viewport jitter
          // or resize merely because a different field/scroll position is active.
          fitOpening(view,true);session.needsReveal=true;
        }
        if (session.phase==='opening') {
          fitOpening(view);
          cancelTimer('openingTimer');
          session.openingTimer=window.setTimeout(()=>{
            if (!session || session.phase!=='opening') return;
            session.phase='active';session.openingTimer=0;revealInput();
          },100);
        }
      } else if (heightLoss<=threshold() && session.phase!=='preparing') {
        if (session.touching) { cancelTimer('recoveryTimer'); return; }
        if (!session.recoveryTimer) session.recoveryTimer=window.setTimeout(()=>{
          if (!session) return;
          session.recoveryTimer=0;
          const latest=visibleViewport();
          if (fullHeight-latest.height<=threshold() && !session.touching) {
            fullHeight=latest.height;restore();
          }
        },100);
        return;
      }
      // Initial native keyboard reveal gets the first turn. Later explicit
      // field changes reveal only within the settings scroller, never the page.
      if (session.phase==='active' && session.needsReveal) { session.needsReveal=false;revealInput(); }
    }
    function schedule() { if (!frame && !disposed) frame=requestAnimationFrame(position); }
    function reveal(node) {
      if (!enabled() || !isEditable(node) || visibleViewport().scale>1.02) return false;
      // Reclaim the closed panel's canvas before capturing its natural size.
      // Diagram links can focus fields without an earlier outside pointer tap.
      state.previewMenu?.close();
      input=node;
      if (!session) {
        const view=visibleViewport();
        const alreadyOpen=fullHeight-view.height>threshold()+12;
        if (!alreadyOpen) {fullHeight=view.height;rememberClosedPreview();}
        // Fine-pointer/hardware-keyboard users retain the normal page. An
        // actual reduced viewport can still start a session on either device.
        if (touchKeyboardDevice() || (alreadyOpen && viewport && view.height<window.innerHeight-80)) begin(view,alreadyOpen);
      }
      if (session) {
        session.manual=false;session.needsReveal=true;
        cancelTimer('recoveryTimer');
      }
      schedule();return !!session;
    }
    function viewportChanged() {
      const view=visibleViewport();
      if (Math.abs(window.innerWidth-viewportWidth)>1) {
        const previousWidth=viewportWidth,wasOpen=session && (session.phase==='opening' || session.phase==='active');
        viewportWidth=window.innerWidth;
        state.previewMenu?.close();
        finish(false);
        closedPreview=null;
        fullHeight=Math.max(window.innerHeight,view.height,wasOpen?Math.min(previousWidth,fullHeight):0);
        if (rendered() && wasOpen) begin(view,true);
      }
      // Only actual viewport resizing changes the shell rectangle. Navigation
      // scrolling has no path to its top or preview size.
      if (session) shellGeometry(view);
      schedule();
    }
    function manualScroll() {
      if (!session || session.phase==='restoring') return;
      session.manual=true;session.needsReveal=false;
    }
    controls.addEventListener('focusin',event=>reveal(event.target),options);
    controls.addEventListener('focusout',event=>{
      if (event.target!==input) return;
      if (isEditable(event.relatedTarget)) reveal(event.relatedTarget);
      // Moving into a modal or leaving the builder must not trap its controls
      // behind the keyboard shell. A null target may just be native Done.
      else if (event.relatedTarget && !controls.contains(event.relatedTarget) && !media()?.contains(event.relatedTarget)) finish(false);
    },options);
    controls.addEventListener('click',event=>{if(event.target===document.activeElement && isEditable(event.target))reveal(event.target);},options);
    document.addEventListener('focusin',event=>{
      if (session && !controls.contains(event.target) && !media()?.contains(event.target)) finish(false);
    },options);
    root.addEventListener('touchstart',()=>{if(session){session.touching=true;cancelTimer('recoveryTimer');}},options);
    const endTouch=event=>{if(session){session.touching=!!event.touches?.length;schedule();}};
    window.addEventListener('touchend',endTouch,options);
    window.addEventListener('touchcancel',endTouch,options);
    information()?.addEventListener('touchmove',manualScroll,options);
    information()?.addEventListener('wheel',manualScroll,options);
    window.addEventListener('resize',viewportChanged,options);
    viewport?.addEventListener('resize',viewportChanged,options);
    viewport?.addEventListener('scroll',()=>{
      // Native focus can still pan the visual viewport once after layout.
      // Correct only the shell coordinate: no canvas resize or focus reveal.
      if (session) setStyle(shellProperties[0],`${visibleViewport().top}px`);
    },options);
    window.addEventListener('pagehide',()=>finish(false),options);
    // Deliberately no document scroll correction loop. The browser scrolls
    // the finite settings row; the preview is its fixed sibling.
    stacked.addEventListener('change',()=>{finish(false);input=null;},options);
    root.removeAttribute('data-swishcb-keyboard-preview-unpinned');
    const destroy=()=>{disposed=true;finish(false);cancelAnimationFrame(frame);frame=0;input=null;};
    return {reveal,destroy};
  }

  function overhang(root) {
    const host = root.querySelector('[data-swishcb-embedded-hoop-details]');
    if (!host) return;
    // Keep the existing 2x overhang illustration scale relative to a 3.05 m rim.
    // Crop the supplied artwork in SVG: the support and basket keep their shape,
    // while only the straight arm stretches as the customer changes the reach.
    const spanPerMeter = 106.25 / 1.1811;
    const rimY = 55;
    const groundY = rimY + spanPerMeter * 3.05 / 2;
    // Source landmarks: base front x=619, pole front x=591, board front x=1062, rim y=315,
    // ground y=974. All fixed parts use the same scale on both axes.
    const artworkScale = (groundY - rimY) / (974 - 315);
    const baseFace = 102.5;
    const poleFace = baseFace - (619 - 591) * artworkScale;
    const artworkTop = groundY + (100 - 974) * artworkScale;
    const boardGuideY = groundY + (375 - 974) * artworkScale;
    const dimensionY = groundY + 25;
    const borderY = dimensionY + 33;
    const guideEndY = dimensionY + 7;
    const viewBoxHeight = groundY + 53;
    const dimensionArrow = (start, end, y) => {
      const head = Math.min(7, Math.max(0, end - start) / 3);
      return `M${start} ${y}H${end}M${start + head} ${y - 5}L${start} ${y}L${start + head} ${y + 5}M${end - head} ${y - 5}L${end} ${y}L${end - head} ${y + 5}`;
    };
    let figure = host.querySelector('.swishcb__overhang-guide');
    if (!figure) {
      figure = make('div', 'swishcb__overhang-guide');
      const supportClipId = `${root.id}-overhang-support-clip`;
      figure.innerHTML = `
        <div class="swishcb__section-heading"><div class="swishcb__panel-summary-title"><h2 class="swishcb__panel-title">Set Up Basketball Lines</h2><span class="swishcb__nav-description">Enter your hoop <span class="swishcb__overhang-info-label">overhang. <button type="button" class="swishcb__tt swishcb__overhang-info" data-swishcb-tooltip="court.overhang_help" data-swishcb-tooltip-toggle aria-label="Hoop overhang information"><svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="10" cy="10" r="8"/><path d="M10 9v6"/><circle cx="10" cy="6" r=".8" fill="currentColor" stroke="none"/></svg></button></span></span></div></div>
        <svg viewBox="0 0 320 ${viewBoxHeight}" preserveAspectRatio="xMidYMid meet" role="group" aria-label="Side view: horizontal overhang from the front edge of the hoop base to the front face of the backboard">
          <rect data-overhang-floor x="102.5" y="${groundY - 1}" width="209.5" height="13" rx="2" fill="#e7e7e9"/>
          <path data-overhang-ground d="M12 ${groundY}H312" stroke="#bdbdc2" stroke-width="1.5"/>
          <g data-overhang-artwork style="mix-blend-mode:darken" aria-hidden="true" pointer-events="none">
            <svg data-overhang-support x="${baseFace + (244 - 619) * artworkScale}" y="${artworkTop}" width="${376 * artworkScale}" height="${875 * artworkScale}" viewBox="244 100 376 875" overflow="hidden">
              <defs><clipPath id="${supportClipId}"><path d="M244 100H591V232H620V975H244Z"/></clipPath></defs>
              <image data-overhang-image style="mix-blend-mode:darken" width="1448" height="1086" clip-path="url(#${supportClipId})"/>
            </svg>
            <svg data-overhang-arm y="${groundY + (180 - 974) * artworkScale}" height="${60 * artworkScale}" viewBox="600 180 400 60" preserveAspectRatio="none" overflow="hidden">
              <image data-overhang-image style="mix-blend-mode:darken" width="1448" height="1086"/>
            </svg>
            <svg data-overhang-hardware y="${artworkTop}" width="${264 * artworkScale}" height="${367 * artworkScale}" viewBox="1004 100 264 367" overflow="hidden">
              <image data-overhang-image style="mix-blend-mode:darken" width="1448" height="1086"/>
            </svg>
          </g>
          <g data-overhang-border display="none">
            <rect data-overhang-border-fill y="${groundY + .75}" height="11.25" fill="#dddde1"/>
            <path data-overhang-border-guides fill="none" stroke="#929299" stroke-width="1" stroke-dasharray="3 3"/>
            <g data-overhang-action="border" role="button" tabindex="0" aria-label="Edit court border" aria-controls="swishcb-main-options-panel">
              <rect class="swishcb__dimension-hit" rx="3"/>
              <path data-overhang-border-dimension fill="none" stroke="#29292e" stroke-width="1.75"/>
              <text data-overhang-border-value y="${borderY - 7}" text-anchor="middle" fill="#29292e" font-size="12.5" font-weight="500"></text>
            </g>
            <text data-overhang-border-label y="${borderY + 21}" text-anchor="end" fill="#727279" font-size="12.5">Border</text>
          </g>
          <path data-overhang-guides d="M102.5 ${groundY + 3}V${guideEndY}M208.75 ${boardGuideY}V${guideEndY}" stroke="#929299" stroke-width="1" stroke-dasharray="3 3"/>
          <g data-overhang-action="overhang" role="button" tabindex="0" aria-label="Edit hoop overhang">
            <rect class="swishcb__dimension-hit" rx="3"/>
            <path data-overhang-dimension d="${dimensionArrow(102.5, 208.75, dimensionY)}" fill="none" stroke="#29292e" stroke-width="1.75"/>
            <text data-overhang-value x="155.625" y="${dimensionY - 7}" text-anchor="middle" fill="#29292e" font-size="12.5" font-weight="500">Overhang</text>
          </g>
          <g text-anchor="middle">
            <text data-overhang-base-label x="${baseFace - 4}" y="${dimensionY + 21}" text-anchor="end" fill="#727279" font-size="12.5">Base</text>
            <text data-overhang-board-label x="212.75" y="${dimensionY + 21}" text-anchor="start" fill="#727279" font-size="12.5">Backboard</text>
          </g>
        </svg>`;
      figure.querySelectorAll('[data-overhang-image]').forEach(image => {
        image.setAttribute('href', root.dataset.swishcbOverhangIllustration);
      });
      host.appendChild(figure);
    }
    if (!figure.dataset.swishcbOverhangBound) {
      figure.dataset.swishcbOverhangBound = 'true';
      const activateMeasurement = event => {
        const action = event.target.closest('[data-overhang-action]');
        if (!action || (event.type === 'keydown' && !['Enter', ' '].includes(event.key))) return;
        event.preventDefault();
        if (action.dataset.overhangAction === 'overhang') {
          focusMeasurementField(root.querySelector(`#swishcb-${host.dataset.swishcbOverhangSide}-hoop-distance`));
          return;
        }
        const panel = root.querySelector('#swishcb-main-options-panel');
        const group = panel?.querySelector('[data-main-option-group="borders"]');
        const side = figure.querySelector(':scope > svg').dataset.borderSide;
        if (!group || !side) return;
        openPanel(panel.closest('details[data-swishcb-panel]'));
        if (panel.hidden) root.querySelector('#swishcb-main-options-toggle')?.click();
        focusMeasurementField(root.querySelector(`#swishcb-${side}-border-size`));
      };
      figure.addEventListener('click', activateMeasurement);
      figure.addEventListener('keydown', activateMeasurement);
    }
    if (host.firstElementChild !== figure) host.prepend(figure);
    const rows = [...host.querySelectorAll('[data-hoop-distance-row]')].filter(row => !row.hidden && row.style.display !== 'none' && row.getAttribute('aria-hidden') !== 'true' && row.dataset.swishcbLayoutHidden !== 'true');
    const singleHoop = rows.length === 1;
    figure.hidden = !rows.length;
    host.dataset.swishcbOverhangVisible = String(!figure.hidden);
    host.dataset.swishcbSingleOverhang = String(singleHoop);
    ['left', 'right', 'top', 'bottom'].forEach(side => {
      const label = host.querySelector(`[data-hoop-distance-row-${side}] .swishcb__tt--label`);
      const title = singleHoop ? 'Enter your hoop overhang' : `${side[0].toUpperCase()}${side.slice(1)} hoop`;
      text(label, title);
      if (label) label.setAttribute('aria-label', `${title} information`);
    });
    if (figure.hidden) return;
    // With several hoops, illustrate the last field the customer focused or edited.
    const inputs = rows.map(row => row.querySelector('[data-hoop-distance]')).filter(Boolean);
    const input = inputs.find(node => node.dataset.hoopDistance === host.dataset.swishcbOverhangSide) || inputs[0];
    if (!input) return;
    host.dataset.swishcbOverhangSide = input.dataset.hoopDistance;
    const raw = input.value.trim().replace(',', '.');
    const value = Number(raw);
    if (!raw || !Number.isFinite(value) || value < 0) return;
    const unit = input.closest('[data-unit]')?.dataset.unit || 'cm';
    const meters = value * (unit === 'ft' ? .3048 : .01);
    // Keep the pole, board and basket shapes intact; only the arm's reach changes.
    const span = spanPerMeter * meters;
    const boardFace = baseFace + span;
    const hardwareLeft = boardFace - (1062 - 1004) * artworkScale;
    const middle = (baseFace + boardFace) / 2;
    const svg = figure.querySelector(':scope > svg');
    const set = (query, name, next) => {
      const node = svg.querySelector(query);
      if (node.getAttribute(name) !== String(next)) node.setAttribute(name, next);
    };
    // Slightly overlap the cropped pieces to avoid subpixel seams at either join.
    set('[data-overhang-arm]', 'x', poleFace - .25);
    set('[data-overhang-arm]', 'width', Math.max(0, hardwareLeft - poleFace + .5));
    set('[data-overhang-hardware]', 'x', hardwareLeft);
    // Read the resolved preview border, including tile rounding and physical hoop-side mapping.
    const border = window.SwishCourtBuilder?.getHoopOverhangDiagramState?.(input.dataset.hoopDistance);
    const poleInside = !!root.querySelector('#swishcb-pole-inside-court')?.checked;
    const showBorder = poleInside && !!border?.enabled && border.borderM > 0;
    const extraHeight = showBorder ? 33 : 0;
    // Reach stays on the upper row; an enabled pole border uses the lower row.
    const overhangY = dimensionY;
    const overhangGuideEndY = guideEndY + extraHeight;
    const endpointLabelY = overhangY + extraHeight + 21;
    // Both illustrated measurements meet at the front edge of the hoop base.
    const borderEnd = baseFace;
    const actualBorderStart = borderEnd - (showBorder ? border.borderM * spanPerMeter : 0);
    // Keep very large existing borders from reducing the whole diagram to an
    // unreadable speck. Shorten only the segment behind the base, never its reach.
    const borderStart = showBorder ? Math.max(actualBorderStart, baseFace - 120) : actualBorderStart;
    const shortenedBorder = showBorder && borderStart > actualBorderStart + .01;
    const floorEnd = Math.max(312, boardFace + 60);
    // The border and playing surface meet at the base's front edge.
    const floorStart = baseFace;
    const borderDisplay = showBorder ? `${Number((border.borderM / (unit === 'ft' ? .3048 : .01)).toFixed(2))} ${unit}` : '';
    set('[data-overhang-border]', 'display', showBorder ? 'inline' : 'none');
    set('[data-overhang-border]', 'aria-hidden', showBorder ? 'false' : 'true');
    set('[data-overhang-floor]', 'x', floorStart);
    set('[data-overhang-floor]', 'width', floorEnd - floorStart);
    set('[data-overhang-ground]', 'd', `M${showBorder ? Math.min(12, borderStart) : 12} ${groundY}H${floorEnd}`);
    let baseGuide = `M${baseFace} ${groundY + 3}V${overhangGuideEndY}`;
    if (showBorder) {
      set('[data-overhang-border-fill]', 'x', borderStart);
      set('[data-overhang-border-fill]', 'width', borderEnd - borderStart);
      // The shared base guide serves both arrows; do not draw a second line
      // through the gap that protects narrow-border measurement text.
      let borderGuide = `M${borderStart} ${groundY + 3}V${borderY + 7}`;
      set('[data-overhang-border-dimension]', 'd', dimensionArrow(borderStart, borderEnd, borderY));
      set('[data-overhang-border-value]', 'x', (borderStart + borderEnd) / 2);
      set('[data-overhang-border-label]', 'x', borderStart - 4);
      text(svg.querySelector('[data-overhang-border-value]'), borderDisplay);
      // Keep the base guide from crossing the text for a narrow border.
      const label = svg.querySelector('[data-overhang-border-value]').getBBox();
      if (baseFace > label.x - 2 && baseFace < label.x + label.width + 2) {
        baseGuide = `M${baseFace} ${groundY + 3}V${label.y - 2}M${baseFace} ${label.y + label.height + 2}V${overhangGuideEndY}`;
      }
      if (borderStart > label.x - 2 && borderStart < label.x + label.width + 2) {
        borderGuide = `M${borderStart} ${groundY + 3}V${label.y - 2}M${borderStart} ${label.y + label.height + 2}V${borderY + 7}`;
      }
      set('[data-overhang-border-guides]', 'd', borderGuide);
    }
    set('[data-overhang-guides]', 'd', `${baseGuide}M${boardFace} ${boardGuideY}V${guideEndY}`);
    set('[data-overhang-dimension]', 'd', dimensionArrow(baseFace, boardFace, overhangY));
    set('[data-overhang-value]', 'x', middle);
    set('[data-overhang-value]', 'y', overhangY - 7);
    set('[data-overhang-base-label]', 'y', endpointLabelY);
    set('[data-overhang-board-label]', 'x', boardFace + 4);
    set('[data-overhang-board-label]', 'y', endpointLabelY);
    const display = `${Number(value.toFixed(2))} ${unit}`;
    const side = input.dataset.hoopDistance;
    text(svg.querySelector('[data-overhang-value]'), display);
    const overhangAction = svg.querySelector('[data-overhang-action="overhang"]');
    overhangAction.setAttribute('aria-label', `Edit ${singleHoop ? 'hoop' : side + ' hoop'} overhang: ${display}`);
    overhangAction.setAttribute('aria-controls', input.id);
    const borderAction = svg.querySelector('[data-overhang-action="border"]');
    borderAction.setAttribute('tabindex', showBorder ? '0' : '-1');
    borderAction.setAttribute('aria-label', `Edit ${border?.side || side} court border: ${borderDisplay}`);
    [overhangAction, ...(showBorder ? [borderAction] : [])].forEach(action => {
      const bounds = action.querySelector('text').getBBox();
      const hit = action.querySelector('.swishcb__dimension-hit');
      Object.entries({x: bounds.x - 8, y: bounds.y - 3, width: bounds.width + 16, height: bounds.height + 6})
        .forEach(([name, next]) => hit.setAttribute(name, next));
    });
    const viewBoxX = showBorder ? Math.min(0, svg.querySelector('[data-overhang-border-label]').getBBox().x - 8) : 0;
    svg.setAttribute('viewBox', `${viewBoxX} 0 ${Math.max(320, boardFace + 78) - viewBoxX} ${viewBoxHeight + extraHeight}`);
    svg.setAttribute('aria-label', `${singleHoop ? 'Hoop' : side + ' hoop'} overhang: ${display}, measured horizontally from the front edge of the hoop base to the front face of the backboard${showBorder ? `; ${border.side} court border: ${borderDisplay}, shown from the outer court edge to the front edge of the hoop base` : ''}${shortenedBorder ? '; border segment shortened in this illustration; the measurement is exact' : ''}`);
    svg.dataset.overhangMeters = String(meters);
    svg.dataset.borderMeters = String(showBorder ? border.borderM : 0);
    svg.dataset.borderSide = showBorder ? border.side : '';
  }

  function courtSizeGuide(root, content) {
    let guide = content.querySelector('.swishcb__court-size-guide');
    if (!guide) {
      guide = make('div', 'swishcb__court-size-guide');
      guide.innerHTML = `
        <div class="swishcb__court-diagram">
          <canvas width="720" height="500" aria-hidden="true"></canvas>
          <svg viewBox="0 0 360 250" preserveAspectRatio="xMidYMid meet" role="group" aria-label="Court dimensions">
            <path data-court-outline fill="none" stroke="#56565c" stroke-width="2"/>
            <path data-court-guides fill="none" stroke="#929299" stroke-width="1" stroke-dasharray="3 3"/>
            <path data-court-arrows fill="none" stroke="#29292e" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
            <g data-court-dimension="width" role="button" tabindex="0" aria-label="Edit your court width"><rect x="-64" y="-22" width="128" height="44" rx="3" class="swishcb__dimension-hit"/><text text-anchor="middle" dy=".35em"></text></g>
            <g data-court-dimension="length" role="button" tabindex="0" aria-label="Edit your court length"><rect x="-64" y="-22" width="128" height="44" rx="3" class="swishcb__dimension-hit"/><text text-anchor="middle" dy=".35em"></text></g>
          </svg>
        </div>
        <div class="swishcb__court-size-fields"></div>`;
      content.appendChild(guide);
    }
    // Native Liquid renders this group in place. Adopt the original nodes only
    // when upgrading an older sidebar, preserving dynamic title updates.
    guide.querySelector(':scope > .swishcb__court-size-heading')?.remove();
    const heading = root.querySelector('.product__info-container > .product__title');
    if (heading) {
      const intro = root.querySelector('.product__info-container > .swishcb__intro');
      let group = guide.querySelector(':scope > .swishcb__builder-intro');
      if (!group) {
        group = make('div', 'swishcb__builder-intro');
        guide.prepend(group);
      }
      group.append(heading);
      if (intro) group.append(intro);
    }
    if (!guide.dataset.swishcbDimensionBound) {
      guide.dataset.swishcbDimensionBound = 'true';
      const focusDimension = event => {
        const dimension = event.target.closest('[data-court-dimension]');
        if (!dimension || (event.type === 'keydown' && !['Enter', ' '].includes(event.key))) return;
        event.preventDefault();
        const input = root.querySelector(`#swishcb-${dimension.dataset.courtDimension}`);
        input?.focus({preventScroll: true});
        input?.select();
      };
      guide.addEventListener('click', focusDimension);
      guide.addEventListener('keydown', focusDimension);
    }
    const fields = guide.querySelector('.swishcb__court-size-fields');
    let previousField = null;
    ['swishcb-width', 'swishcb-length'].forEach(id => {
      const row = root.querySelector(`#${id}`)?.closest('.swishcb__row');
      if (!row) return;
      const reference = previousField ? previousField.nextElementSibling : fields.firstElementChild;
      if (row !== reference) {
        if (fields.moveBefore && row.isConnected) fields.moveBefore(row, reference);
        else fields.insertBefore(row, reference);
      }
      previousField = row;
    });
    return guide;
  }

  // Fit the actual installed surface and both dimension labels together. Using
  // the preview's rotation also keeps width/length attached to the correct edge.
  function courtDiagramLayout(lengthM, widthM, rotationDeg, measurements = true) {
    const angle = rotationDeg * Math.PI / 180;
    const u = {x: Math.cos(angle), y: Math.sin(angle)};
    const v = {x: -u.y, y: u.x};
    const boxW = Math.abs(u.x) * lengthM + Math.abs(v.x) * widthM;
    const boxH = Math.abs(u.y) * lengthM + Math.abs(v.y) * widthM;
    const scale = Math.min(256 / boxW, 176 / boxH);
    const w = lengthM * scale, h = widthM * scale;
    const point = (x, y) => ({x: u.x * x + v.x * y, y: u.y * x + v.y * y});
    const corners = [point(-w/2, -h/2), point(w/2, -h/2), point(w/2, h/2), point(-w/2, h/2)];
    const bounds = [...corners];
    const dimensions = (measurements ? [
      {name: 'length', meters: lengthM, direction: u, normal: v, span: w, depth: h},
      {name: 'width', meters: widthM, direction: v, normal: u, span: h, depth: w}
    ] : []).map(d => {
      const sign = Math.abs(d.normal.y) > 1e-6 ? Math.sign(d.normal.y) : -Math.sign(d.normal.x);
      const n = {x: d.normal.x * sign, y: d.normal.y * sign};
      const at = (along, out) => ({x: d.direction.x * along + n.x * out, y: d.direction.y * along + n.y * out});
      const start = at(-d.span/2, d.depth/2 + 20);
      const end = at(d.span/2, d.depth/2 + 20);
      const label = at(0, d.depth/2 + 34);
      let degrees = Math.atan2(d.direction.y, d.direction.x) * 180 / Math.PI;
      if (degrees > 90) degrees -= 180;
      if (degrees < -90) degrees += 180;
      const c = Math.cos(degrees * Math.PI / 180), s = Math.sin(degrees * Math.PI / 180);
      // Include the complete 44px interactive label target, even for narrow courts.
      for (const x of [-64, 64]) for (const y of [-22, 22]) {
        bounds.push({x: label.x + c*x - s*y, y: label.y + s*x + c*y});
      }
      bounds.push(start, end);
      return {...d, normal: n, start, end, label, degrees,
        guides: [at(-d.span/2, d.depth/2), at(-d.span/2, d.depth/2 + 25),
          at(d.span/2, d.depth/2), at(d.span/2, d.depth/2 + 25)]};
    });
    const minX = Math.min(...bounds.map(p => p.x)) - 10;
    const minY = Math.min(...bounds.map(p => p.y)) - 10;
    return {angle, scale, w, h, corners, dimensions, x: -minX, y: -minY,
      width: Math.ceil((Math.max(...bounds.map(p => p.x)) + 10 - minX) * 2) / 2,
      height: Math.ceil((Math.max(...bounds.map(p => p.y)) + 10 - minY) * 2) / 2};
  }

  // Capture synchronously: render canvases are reused by later paint/export
  // passes. Remove their viewport transform, then crop only the installed floor.
  // No second regulation drawing, geometry approximation or builder-state edits.
  function captureCourtDiagram(state, data, bitmapKey = 'courtBitmap') {
    const r = data.surface;
    if (!data.source || !r || !(r.w > 0 && r.h > 0)) return false;
    const m = data.sourceTransform || {a:1, b:0, c:0, d:1, e:0, f:0};
    const det = m.a*m.d - m.b*m.c;
    if (!Number.isFinite(det) || Math.abs(det) < 1e-9) return false;
    const bitmap = state[bitmapKey] || (state[bitmapKey] = document.createElement('canvas'));
    const scale = Math.min(2, 720 / Math.max(r.w, r.h));
    const bw = Math.max(1, Math.round(r.w * scale));
    const bh = Math.max(1, Math.round(r.h * scale));
    if (bitmap.width !== bw) bitmap.width = bw;
    if (bitmap.height !== bh) bitmap.height = bh;
    const ctx = bitmap.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, bw, bh);
    const sx = bw/r.w, sy = bh/r.h;
    ctx.setTransform(sx, 0, 0, sy, -r.x*sx, -r.y*sy);
    ctx.transform(m.d/det, -m.b/det, -m.c/det, m.a/det,
      (m.c*m.f-m.d*m.e)/det, (m.b*m.e-m.a*m.f)/det);
    ctx.drawImage(data.source, 0, 0);
    if (typeof data.applyMask === 'function') {
      ctx.setTransform(sx, 0, 0, sy, -r.x*sx, -r.y*sy);
      data.applyMask(ctx);
    }
    return true;
  }

  function captureLogoCourtDiagram(root, snapshot) {
    if (!root?.matches(selector)) return;
    if (!states.has(root)) sync(root);
    const state = states.get(root);
    if (state) captureCourtDiagram(state, snapshot, 'logoCourtBitmap');
  }

  function updateCourtDiagram(root, snapshot) {
    if (!root?.matches(selector) || !(snapshot?.widthM > 0 && snapshot.lengthM > 0)) return;
    if (!states.has(root)) sync(root);
    const state = states.get(root);
    if (!state || !captureCourtDiagram(state, snapshot)) return;
    // Keep only immutable metadata; do not retain the renderer's mutable canvas.
    state.courtSnapshot = {view:snapshot.view, ruleset:snapshot.ruleset, units:snapshot.units, mainColor:snapshot.mainColor,
      hoopCount:snapshot.hoopSides?.length || 0,
      logoTarget:logoDiagramTarget(snapshot),
      widthM:snapshot.widthM, lengthM:snapshot.lengthM, rotationDeg:Number(snapshot.rotationDeg) || 0};
    if (state.courtFrame) return;
    state.courtFrame = requestAnimationFrame(() => {
      state.courtFrame = 0;
      if (!root.isConnected) return;
      overhang(root);
      paintLogoDiagram(root);
      const guide = root.querySelector('.swishcb__court-size-guide');
      if (!guide) return;
      const data = state.courtSnapshot;
      const {widthM, lengthM} = data;
      const f = courtDiagramLayout(lengthM, widthM, data.rotationDeg);
      const canvas = guide.querySelector('canvas');
      canvas.width = Math.ceil(f.width * 2);
      canvas.height = Math.ceil(f.height * 2);
      const ctx = canvas.getContext('2d');
      ctx.setTransform(2, 0, 0, 2, 0, 0);
      ctx.translate(f.x, f.y);
      ctx.rotate(f.angle);
      ctx.drawImage(state.courtBitmap, -f.w/2, -f.h/2, f.w, f.h);
      const svg = guide.querySelector('svg');
      svg.setAttribute('viewBox', `0 0 ${f.width} ${f.height}`);
      const xy = p => `${p.x+f.x} ${p.y+f.y}`;
      svg.querySelector('[data-court-outline]').setAttribute('d', `M${f.corners.map(xy).join('L')}Z`);
      let arrows = '', guides = '';
      const units = data.units === 'ft' ? 'ft' : 'm';
      const unitSelect = root.querySelector('#swishcb-units-select');
      if (unitSelect && unitSelect.value !== units) unitSelect.value = units;
      const format = meters => `${Number((meters / (units === 'ft' ? .3048 : 1)).toFixed(units === 'ft' ? 2 : 3))} ${units}`;
      for (const d of f.dimensions) {
        const a = Math.min(5, d.span/4);
        const arrow = (p, direction) => {
          const q = {x:p.x+d.direction.x*a*direction, y:p.y+d.direction.y*a*direction};
          return `M${xy({x:q.x+d.normal.x*a,y:q.y+d.normal.y*a})}L${xy(p)}L${xy({x:q.x-d.normal.x*a,y:q.y-d.normal.y*a})}`;
        };
        arrows += `M${xy(d.start)}L${xy(d.end)}${arrow(d.start, 1)}${arrow(d.end, -1)}`;
        guides += `M${xy(d.guides[0])}L${xy(d.guides[1])}M${xy(d.guides[2])}L${xy(d.guides[3])}`;
        const label = svg.querySelector(`[data-court-dimension="${d.name}"]`);
        label.setAttribute('transform', `translate(${xy(d.label)}) rotate(${d.degrees})`);
        text(label.querySelector('text'), `${d.name[0].toUpperCase()+d.name.slice(1)} · ${format(d.meters)}`);
        label.setAttribute('aria-label', `Edit your court ${d.name}, ${format(d.meters)}`);
      }
      svg.querySelector('[data-court-arrows]').setAttribute('d', arrows);
      svg.querySelector('[data-court-guides]').setAttribute('d', guides);
      const court = selectedText(root.querySelector('#court-xxx'));
      svg.setAttribute('aria-label', `${court} court: ${format(widthM)} wide by ${format(lengthM)} long. Select a measurement to edit it.`);
      guide.dataset.courtType = data.ruleset;
      guide.dataset.courtWidth = String(widthM);
      guide.dataset.courtLength = String(lengthM);
      guide.dataset.courtRotation = String(data.rotationDeg);
      guide.dataset.courtView = data.view;
      guide.dataset.courtReady = 'true';
    });
  }

  function sportsRows(root) {
    root.querySelectorAll('[data-extra-card]').forEach(card => {
      const label = card.querySelector(':scope > .swishcb__row > .swishcb__row-label')?.textContent.trim().replace(/\s+/g, ' ') || 'Sport';
      const settings = card.querySelector('[data-extra-panel-toggle]');
      text(settings?.querySelector('.swishcb__extra-toggle-label'), 'Settings');
      settings?.setAttribute('aria-label', `${label} settings`);
      card.querySelector('[data-extra-hex]')?.setAttribute('aria-label', `${label} color`);
    });
  }

  /* Tennis and volleyball seam paths adapted from Tabler Icons:
   * https://github.com/tabler/tabler-icons
   * MIT License — Copyright (c) 2020-2026 Paweł Kuna
   *
   * Permission is hereby granted, free of charge, to any person obtaining a copy
   * of this software and associated documentation files (the "Software"), to deal
   * in the Software without restriction, including without limitation the rights
   * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
   * copies of the Software, and to permit persons to whom the Software is
   * furnished to do so, subject to the following conditions:
   *
   * The above copyright notice and this permission notice shall be included in all
   * copies or substantial portions of the Software.
   *
   * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
   * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
   * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
   * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
   * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
   * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
   * SOFTWARE.
   */
  let sportBallSequence = 0;
  // Draw the outline last and clip seams to the same circle, so their ends can
  // never distort the silhouette. Each rendered SVG receives its own clip ID.
  function roundSportBall(seams) {
    return clipId => '<defs><clipPath id="' + clipId + '" clipPathUnits="userSpaceOnUse"><circle cx="40" cy="40" r="25" fill="#fff" stroke="none"/></clipPath></defs><g clip-path="url(#' + clipId + ')">' + seams + '</g><circle class="swishcb__sport-ball-outline" cx="40" cy="40" r="25"/>';
  }
  // Every shortcut uses the existing checkbox and Settings disclosure. The main
  // court is informational: its geometry is owned by the court-type selector.
  const sportArtwork = {
    basketball: ['Basketball', roundSportBall('<path d="M40 15V65M15 40H65M22.322 22.322Q42 40 22.322 57.678M57.678 22.322Q38 40 57.678 57.678"/>')],
    pickleball: ['Pickleball', '<g transform="rotate(14 31 38)"><path d="M25 9H39Q48 9 48 19V42Q48 51 39 54L36 59V72H28V59L25 54Q16 51 16 42V19Q16 9 25 9Z"/></g><circle cx="62" cy="43" r="10"/><g fill="currentColor" stroke="none"><circle cx="62" cy="37" r="1.4"/><circle cx="57" cy="41" r="1.4"/><circle cx="67" cy="41" r="1.4"/><circle cx="59" cy="47" r="1.4"/><circle cx="65" cy="47" r="1.4"/></g>'],
    volleyball: ['Volleyball', roundSportBall('<g transform="translate(40 40) scale(2.777778) translate(-12 -12)"><path d="M12 12a8 8 0 0 0 8 4M7.5 13.5a12 12 0 0 0 8.5 6.5M12 12a8 8 0 0 0-7.464 4.928M12.951 7.353a12 12 0 0 0-9.88 4.111M12 12a8 8 0 0 0-.536-8.928M15.549 15.147a12 12 0 0 0 1.38-10.611"/></g>')],
    netball: ['Netball', roundSportBall('<path d="M28 23Q40 18 52 23Q60 40 52 57Q40 62 28 57Q20 40 28 23ZM24.7 34Q40 29 55.3 34M24.7 46Q40 51 55.3 46M28 23L22.322 22.322M52 23L57.678 22.322M28 57L22.322 57.678M52 57L57.678 57.678M22.322 22.322Q11 40 22.322 57.678M57.678 22.322Q69 40 57.678 57.678M40 15V20.5M40 59.5V65"/>')],
    tennis: ['Tennis', roundSportBall('<path d="M23.3 21.4a25 25 0 0 1 0 37.2M56.7 21.4a25 25 0 0 0 0 37.2"/>')],
    padel: ['Padel', '<g transform="rotate(17 35 39)"><path d="M15 30C15 4 53 4 53 30C53 42 47 50 39 54L38 72H30L29 54C21 50 15 42 15 30Z"/><path d="M25 50L34 59L43 50"/><g fill="currentColor" stroke="none"><circle cx="26" cy="23" r="1.7"/><circle cx="34" cy="23" r="1.7"/><circle cx="42" cy="23" r="1.7"/><circle cx="26" cy="31" r="1.7"/><circle cx="34" cy="31" r="1.7"/><circle cx="42" cy="31" r="1.7"/><circle cx="26" cy="39" r="1.7"/><circle cx="34" cy="39" r="1.7"/><circle cx="42" cy="39" r="1.7"/></g></g><circle cx="64" cy="54" r="7"/>'],
    racquetball: ['Racquetball', '<g transform="rotate(18 34 38)"><ellipse cx="34" cy="29" rx="18" ry="22"/><path d="M22 46L30 57V72H38V57L46 46M25 13V45M34 8V50M43 13V45M18 20H50M16 29H52M18 38H50"/></g><circle cx="63" cy="55" r="7"/>'],
    shuffleboard: ['Shuffleboard', '<path d="M34 9L48 53M42 53L54 50L59 61M42 53L44 64"/><ellipse cx="27" cy="59" rx="15" ry="6"/><path d="M12 59V65C12 73 42 73 42 65V59M22 57H32"/>'],
    cornhole: ['Cornhole', '<path d="M27 12H60L51 66H11Z"/><ellipse cx="40" cy="27" rx="7" ry="6"/><path d="M60 12L65 59H53M56 52L67 47L74 57L63 63ZM26 47L37 43L43 53L32 58Z"/>'],
    futsal: ['Futsal', roundSportBall('<path d="M40 29L51 37L47 50H33L29 37ZM40 15V29M64 33L51 37M55 60L47 50M25 60L33 50M16 33L29 37M26 19L25 28L16 33M54 19L55 28L64 33M63 50L55 51L55 60M49 63L40 58L31 63M17 50L25 51L25 60"/>')]
  };
  const suggestedSports = ['pickleball', 'volleyball', 'netball', 'tennis'];
  const sportShortcuts = new WeakMap();

  function refreshSports(root) {
    const row = root?.querySelector('.swishcb__sports-shortcuts');
    if (!row) return;
    const court = root.querySelector('#court-xxx');
    const type = court?.value || 'fiba';
    const main = ['fiba', 'nba', 'ncaa', 'usahs'].includes(type) ? 'basketball' : type;
    if (!sportArtwork[main]) return;
    const toggles = [...root.querySelectorAll('[data-extra-toggle]')].filter(input =>
      sportArtwork[input.dataset.extraToggle.split('-')[0]] &&
      window.SwishCBConfig.isExtraSportAllowedForCourt(input.dataset.extraToggle, type));
    const available = [...new Set(toggles.map(input => input.dataset.extraToggle.split('-')[0]))];
    const active = [...new Set(toggles.filter(input => input.checked).map(input => input.dataset.extraToggle.split('-')[0]))];
    let state = sportShortcuts.get(root);
    if (!state) {
      state = {previous: [], recent: [], slots: [], nodes: new Map(), signature: ''};
      sportShortcuts.set(root, state);
    }
    active.filter(id => !state.previous.includes(id)).forEach(id => {
      state.recent = state.recent.filter(item => item !== id).concat(id);
    });
    state.previous = active;
    state.recent = state.recent.filter(id => active.includes(id));
    // Four optional slots: keep their positions stable and replace an inactive
    // suggestion first. With more than four enabled sports, show the latest four;
    // every enabled overlay remains available in the full menu.
    const wanted = state.recent.filter(id => id !== main).slice(-4);
    const defaults = [...new Set([...suggestedSports, ...available])].filter(id => id !== main && available.includes(id));
    const displayed = [...wanted, ...defaults.filter(id => !wanted.includes(id))].slice(0, 4);
    const slots = state.slots.map(id => displayed.includes(id) ? id : null);
    while (slots.length < 4) slots.push(null);
    wanted.forEach(id => {
      if (slots.includes(id)) return;
      const index = slots.findIndex(item => !wanted.includes(item));
      if (index !== -1) slots[index] = id;
    });
    defaults.forEach(id => {
      if (slots.includes(id)) return;
      const index = slots.indexOf(null);
      if (index !== -1) slots[index] = id;
    });
    state.slots = slots;
    const ids = [slots[0], slots[1], main, slots[2], slots[3]].filter(Boolean);
    const title = selectedText(court) || sportArtwork[main][0];
    const signature = JSON.stringify([ids, active, title]);
    if (state.signature === signature) return;
    state.signature = signature;
    const nodes = ids.map((id, index) => {
      const primary = index === 2;
      const key = (primary ? 'main-' : 'extra-') + id;
      let item = state.nodes.get(key);
      if (!item) {
        item = make(primary ? 'div' : 'button', 'swishcb__sport-shortcut');
        item.dataset.sportIcon = id;
        const stem = {pickleball: [21, 72], padel: [24, 72], racquetball: [24, 73], shuffleboard: [27, 71], cornhole: [28, 66]}[id] || [40, 65];
        const artwork = sportArtwork[id][1];
        const drawing = typeof artwork === 'function' ? artwork('swishcb-sport-ball-' + (++sportBallSequence)) : artwork;
        item.innerHTML = '<svg viewBox="0 0 80 116" preserveAspectRatio="xMidYMid meet" aria-hidden="true" focusable="false"><path class="swishcb__sport-stem" d="M' + stem[0] + ' ' + stem[1] + 'V116"/><g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">' + drawing + '</g></svg>';
        if (primary) {
          item.dataset.sportMain = 'true';
          item.setAttribute('role', 'img');
        } else {
          item.type = 'button';
          item.addEventListener('click', () => {
            cancelAnimationFrame(state.scrollFrame);
            const menu = root.querySelector('#swishcb-add-more-sports-panel');
            const menuToggle = root.querySelector('[data-action="toggle-add-more-sports"]');
            const inputs = [...root.querySelectorAll('[data-extra-toggle]')].filter(input => input.dataset.extraToggle.split('-')[0] === id);
            const enabled = inputs.filter(input => input.checked && !input.disabled);
            if (enabled.length) {
              const slot = [...row.children].indexOf(item);
              // One icon represents every enabled instance of that sport.
              enabled.reverse().forEach(input => {
                const settings = input.closest('[data-extra-card]')?.querySelector('[data-extra-panel-toggle]');
                if (settings?.getAttribute('aria-expanded') === 'true') settings.click();
                input.click();
              });
              if (menu && !menu.hidden) menuToggle?.click();
              refreshSports(root);
              if (!item.isConnected) row.children[slot]?.focus({preventScroll: true});
              return;
            }
            const input = inputs[0];
            if (!input || input.disabled) return;
            if (menu?.hidden && menuToggle) {
              menuToggle.dataset.swishcbSuppressScrollOnce = 'true';
              menuToggle.click();
            }
            input.click();
            const card = input.closest('[data-extra-card]');
            const settings = card?.querySelector('[data-extra-panel-toggle]');
            if (settings?.getAttribute('aria-expanded') !== 'true') settings?.click();
            refreshSports(root);
            // Let checkbox-driven layout updates settle before positioning the
            // selected card. A second click cancels this pending navigation.
            state.scrollFrame = requestAnimationFrame(() => {
              state.scrollFrame = requestAnimationFrame(() => {
                state.scrollFrame = 0;
                if (!card?.isConnected || !input.checked || menu?.hidden) return;
                settings?.focus({preventScroll: true});
                card.scrollIntoView({block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
              });
            });
          });
        }
        state.nodes.set(key, item);
      }
      const selected = primary || active.includes(id);
      item.dataset.selected = String(selected);
      const label = primary ? title + ' — main court (always selected)' : sportArtwork[id][0];
      item.setAttribute('aria-label', label);
      item.title = primary ? label : (selected ? 'Remove ' + sportArtwork[id][0].toLowerCase() + ' lines' : 'Add ' + sportArtwork[id][0].toLowerCase() + ' lines and open settings');
      if (!primary) item.setAttribute('aria-pressed', String(selected));
      return item;
    });
    // Do not replace focused buttons when state, Undo or the main court changes.
    [...row.children].filter(node => !nodes.includes(node)).forEach(node => node.remove());
    nodes.forEach((node, index) => {
      if (row.children[index] !== node) {
        if (row.moveBefore && node.isConnected) row.moveBefore(node, row.children[index] || null);
        else row.insertBefore(node, row.children[index] || null);
      }
    });
  }

  function sizeSportsOptions(root, guide) {
    const panel = root.querySelector('[data-swishcb-panel="add-more-sports"]');
    const content = contentOf(panel);
    const toggle = root.querySelector('[data-action="toggle-add-more-sports"]');
    const options = root.querySelector('#swishcb-add-more-sports-panel');
    if (!guide || !content || !toggle || !options) return;
    // Keep the original disclosure and sport controls, now inside Court Size.
    // There is one entry point, without a separate heading or icon section.
    append(content, toggle.closest('.swishcb__row'));
    append(content, options);
    content.querySelector('.swishcb__sports-guide')?.remove();
    guide.querySelector(':scope > .swishcb__size-sports-action')?.remove();
    append(guide, panel);
    panel.dataset.swishcbSizeSports = 'true';
    toggle.id = root.id + '-size-sports-toggle';
    toggle.setAttribute('aria-label', 'Add sports');
    toggle.removeAttribute('data-swishcb-tooltip');
    if (!toggle.dataset.swishcbSizeSportsBound) {
      toggle.dataset.swishcbSizeSportsBound = 'true';
      if (!nativeSidebar(root)) toggle.innerHTML = '<span class="swishcb__more-options-icon" aria-hidden="true">+</span> Add sports';
      toggle.addEventListener('click', () => {
        // The full list is taller than most screens. Keep its button and first
        // choices visible instead of scrolling to the bottom of the list.
        toggle.dataset.swishcbSuppressScrollOnce = 'true';
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (!options.isConnected || options.hidden) return;
          toggle.scrollIntoView({block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
        }));
      }, {capture: true});
    }
    text(toggle.querySelector('.swishcb__more-options-icon'), options.hidden ? '+' : '-');
    options.setAttribute('role', 'region');
    options.setAttribute('aria-labelledby', toggle.id);
    options.querySelector(':scope > .swishcb__hint--intro')?.remove();
    const summary = panel.querySelector(':scope > summary');
    if (summary) { summary.hidden = true; summary.setAttribute('aria-hidden', 'true'); summary.tabIndex = -1; }
    setExpanded(panel, true);
  }

  function sizeFields(root, panel) {
    const content = contentOf(panel);
    if (!content) return;
    const fields = {'court-xxx':'court', 'swishcb-units-select':'units', 'swishcb-width':'width', 'swishcb-length':'length'};
    Object.entries(fields).forEach(([id, name]) => {
      const row = root.querySelector(`#${id}`)?.closest('.swishcb__row');
      if (row) row.dataset.swishcbSidebarField = name;
    });
    root.querySelectorAll('#swishcb-units-select option').forEach(option => text(option, option.value === 'ft' ? 'ft' : 'm'));
    const options = root.querySelector('#swishcb-main-options-panel');
    const existingGuide = content.querySelector(':scope > .swishcb__court-size-guide');
    // Multi-court mode owns these controls while they are in a selected-court panel.
    if (!options || (options.parentNode !== content && options.parentNode !== existingGuide)) return;
    const optionsAction = root.querySelector('.swishcb__main-options-toggle-row');
    const guide = courtSizeGuide(root, content);
    append(guide, optionsAction);
    append(guide, options);
    // Native markup already has this order; retain it in legacy configurators too.
    let previousOption = null;
    ['court-xxx', 'swishcb-units-select'].forEach(id => {
      const row = root.querySelector(`#${id}`)?.closest('.swishcb__row');
      if (!row) return;
      const reference = previousOption ? previousOption.nextElementSibling : options.firstElementChild;
      if (row !== reference) {
        if (options.moveBefore && row.isConnected) options.moveBefore(row, reference);
        else options.insertBefore(row, reference);
      }
      previousOption = row;
    });
    sizeSportsOptions(root, guide);
    root.querySelector('#swishcb-main-options-toggle')?.childNodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) text(node, ' More options');
    });
    // Match keyboard/read order to the visual order after the legacy layout runs.
    // Measurements and the option disclosures share one card.
    const primary = [guide];
    primary.push(root.querySelector('[data-swishcb-embedded-hoop-details]'));
    const ordered = [...primary, ...[...content.children].filter(node => !primary.includes(node))].filter(Boolean);
    let previous = null;
    ordered.forEach(node => {
      const reference = previous ? previous.nextElementSibling : content.firstElementChild;
      if (node !== reference) {
        if (content.moveBefore && node.isConnected) content.moveBefore(node, reference);
        else content.insertBefore(node, reference);
      }
      previous = node;
    });
  }

  function surface(root) {
    const panel = root.querySelector('[data-swishcb-panel="order-now"]');
    const content = contentOf(panel);
    if (!content) return;
    const selected = root.querySelector('#swishcb-tile-series');
    const hardwood = selected?.value.startsWith('hardwood');
    panel.dataset.swishcbSidebarSurface = hardwood ? 'hardwood' : 'tiles';
    let note = content.querySelector('.swishcb__surface-note');
    if (!note) {
      note = make('p', 'swishcb__surface-note');
      const series = selected?.closest('.swishcb__row');
      if (series && content.contains(series)) series.after(note);
      else content.prepend(note);
    }
    note.hidden = !hardwood;
    text(note, `${selectedText(selected)} is quoted to your court size. Request pricing below and our team will help with the details.`);
    let information = content.querySelector('.swishcb__flooring-information');
    let toggle = root.querySelector('.swishcb__flooring-learn-more');
    if (!information) {
      information = make('div', 'swishcb__flooring-information');
      information.id = `${root.id}-flooring-information`;
      information.hidden = true;
      toggle = make('button', 'swishcb__flooring-learn-more', 'More');
      toggle.type = 'button';
      toggle.setAttribute('aria-controls', information.id);
      toggle.setAttribute('aria-expanded', 'false');
      content.append(toggle, information);
    }
    if (toggle && !toggle.dataset.swishcbFlooringBound) {
      toggle.dataset.swishcbFlooringBound = 'true';
      toggle.addEventListener('click', () => {
        information.hidden = !information.hidden;
        toggle.setAttribute('aria-expanded', String(!information.hidden));
        if (information.hidden) information.querySelectorAll('video').forEach(video => video.pause());
      });
    }
    append(information, note);
    append(information, content.querySelector('.swishcb__order-now-content'));
    selected?.setAttribute('aria-label', 'Flooring series');
    const hasFlooring = !!selected?.value && selected.value !== 'none';
    text(toggle, 'More');
    toggle.hidden = !hasFlooring;
    if (!hasFlooring) {
      information.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
    }
  }

  function customSurfaceRequiresQuote(root) {
    if (window.SwishCB?.ordering?.getState?.(root)?.customSurfaceQuote) return true;
    // The settings panel can refresh before the ordering result has caught up.
    // Read the same canonical shape from the builder's serialized configuration.
    try {
      const value = root?.querySelector('[data-bind="config-json"]')?.value;
      const config = value ? JSON.parse(value) : null;
      return Boolean(window.SwishCBCustomSurface?.sanitize(config?.customSurface));
    } catch (_) { return false; }
  }

  function surfaceSupportsCheckout(root) {
    return !customSurfaceRequiresQuote(root) && ['elastic', 'elite', 'large-elite'].includes(root?.querySelector('#swishcb-tile-series')?.value);
  }

  function updatePayment(root) {
    // Remove any legacy selector left behind by a theme-editor refresh.
    root.querySelector('[data-swishcb-payment-options]')?.remove();
    root.dataset.swishcbPaymentPlan = 'full';
    const order = window.SwishCB?.ordering?.getState?.(root);
    const payment = window.SwishCB?.ordering?.getPayment?.(root);
    const orderableSurface = surfaceSupportsCheckout(root);
    root.dataset.swishcbSidebarCheckout = String(orderableSurface);
    const reviewDetails = root.querySelector('.swishcb__review-details');
    if (reviewDetails) reviewDetails.hidden = false;
    const ready = !!payment && order?.eligible && order?.purchasable && orderableSurface;
    const purchase = root.querySelector('.swishcb__purchase');
    const price = purchase?.querySelector('[data-swishcb-product-price]');
    if (!price) { summaryPurchase(root, reviewDetails); return; }
    let breakdown = purchase.querySelector('.swishcb__payment-review');
    if (!breakdown) {
      breakdown = make('div', 'swishcb__payment-review');
      breakdown.innerHTML = `<span class="swishcb__payment-review-amount" data-payment-first></span>
      <span class="swishcb__payment-review-label">Free shipping</span>`;
      price.parentElement.before(breakdown);
    }
    breakdown.hidden = !ready;
    price.parentElement.classList.toggle('swishcb__purchase-price-hidden', !!ready);
    if (ready) price.closest('[role="status"]')?.removeAttribute('aria-label');
    if (ready) {
      const amount = value => window.SwishCBPricing.formatTotal(value, payment.currencyCode, document.documentElement.lang || 'en');
      text(breakdown.querySelector('[data-payment-first]'), amount(payment.dueNow));
    }
    summaryPurchase(root, reviewDetails);
  }

  function summaryPurchase(root, details) {
    if (!details) return;
    let footer = details.querySelector(':scope > .swishcb__summary-purchase');
    if (!footer) {
      footer = make('div', 'swishcb__summary-purchase');
      footer.innerHTML = '<div class="swishcb__summary-total" role="status" aria-live="polite"><span>Total price</span><strong data-swishcb-summary-total></strong><span data-swishcb-summary-shipping>Free shipping</span></div><div class="swishcb__summary-buy-buttons"><button type="button" class="button button--primary button--full-width" data-swishcb-summary-checkout disabled>Order Now</button><button type="button" class="button button--secondary button--full-width" data-swishcb-summary-choose-series hidden>Choose tile series</button><button type="button" class="button button--secondary button--full-width" data-swishcb-summary-contact hidden>Contact us</button></div><p class="swishcb__checkout-notice" data-swishcb-summary-checkout-notice role="status" hidden></p>';
      const help = details.querySelector(':scope > [data-swishcb-panel="contact"]');
      details.insertBefore(footer, help);
      // Both locations invoke the original secure checkout button. There is one
      // form, one pricing source and one checkout handler for the whole builder.
      footer.querySelector('[data-swishcb-summary-checkout]').addEventListener('click', () => {
        const source = root.querySelector('[data-swishcb-v2-secure-checkout]');
        if (source && !source.disabled && source.getAttribute('aria-disabled') !== 'true' && source.getAttribute('aria-busy') !== 'true') source.click();
      });
      footer.querySelector('[data-swishcb-summary-choose-series]').addEventListener('click', () => {
        const series = details.querySelector('[data-swishcb-product-series-trigger]:not(.swishcb__order-edit)');
        if (series) {
          series.scrollIntoView({block: 'nearest', behavior: 'instant'});
          // Scroll events dismiss popovers; open after that scroll is applied.
          requestAnimationFrame(() => { if (series.isConnected) series.click(); });
        }
        else root.querySelector('[data-swishcb-product-price-select-series]')?.click();
      });
      footer.querySelector('[data-swishcb-summary-contact]').addEventListener('click', () => root.querySelector('[data-swishcb-configurator-contact]')?.click());
    }
    const price = root.querySelector('[data-swishcb-product-price]');
    const source = root.querySelector('[data-swishcb-v2-secure-checkout]');
    const state = price?.dataset.swishcbPriceState || 'loading';
    const displayed = root.querySelector('.swishcb__purchase .swishcb__payment-review:not([hidden]) [data-payment-first]');
    const total = footer.querySelector('[data-swishcb-summary-total]');
    text(total, state === 'ready' ? displayed?.textContent.trim() || price?.textContent.trim() : price?.textContent.trim() || 'Loading…');
    total.dataset.swishcbPriceState = state;
    footer.querySelector('[data-swishcb-summary-shipping]').hidden = state !== 'ready';
    const buy = footer.querySelector('[data-swishcb-summary-checkout]');
    const busy = !!source && (source.classList.contains('loading') || source.getAttribute('aria-busy') === 'true');
    buy.hidden = state === 'quote' || state === 'needs-series';
    buy.disabled = !source || source.disabled || source.getAttribute('aria-disabled') === 'true' || busy || state !== 'ready';
    buy.setAttribute('aria-disabled', String(buy.disabled));
    buy.setAttribute('aria-busy', String(busy));
    text(buy, source?.querySelector(':scope > span')?.textContent.trim() || 'Order Now');
    footer.querySelector('[data-swishcb-summary-choose-series]').hidden = state !== 'needs-series';
    const contact = footer.querySelector('[data-swishcb-summary-contact]');
    contact.hidden = state !== 'quote';
    text(contact, state === 'quote' ? 'Request pricing' : 'Contact us');
    const sourceNotice = root.querySelector('[data-swishcb-v2-checkout-content] [data-swishcb-checkout-notice]');
    const notice = footer.querySelector('[data-swishcb-summary-checkout-notice]');
    text(notice, sourceNotice?.textContent || '');
    notice.hidden = !sourceNotice || sourceNotice.hidden || !sourceNotice.textContent.trim();
    const sidebar = states.get(root);
    if (sidebar && source && (sidebar.summaryPurchaseSource !== source || sidebar.summaryPurchaseNotice !== sourceNotice)) {
      sidebar.summaryPurchaseObserver?.disconnect();
      sidebar.summaryPurchaseSource = source;
      sidebar.summaryPurchaseNotice = sourceNotice;
      sidebar.summaryPurchaseObserver = new MutationObserver(() => summaryPurchase(root, details));
      sidebar.summaryPurchaseObserver.observe(source, {attributes: true, attributeFilter: ['disabled','aria-disabled','aria-busy','class'], childList: true, subtree: true, characterData: true});
      if (sourceNotice) sidebar.summaryPurchaseObserver.observe(sourceNotice, {attributes: true, childList: true, subtree: true, characterData: true});
    }
  }

  function review(root, panel) {
    const content = contentOf(panel);
    if (!content) return;
    panel.dataset.swishcbNativeDisclosure = 'true';
    panel.dataset.swishcbSummaryCollapsed = 'false';
    panel.open = true;
    let header = content.querySelector('.swishcb__review-header');
    if (!header) {
      header = make('div', 'swishcb__review-header');
      header.append(make('h2', 'swishcb__review-title', 'Review & order'));
      const toggle = make('button', 'swishcb__review-toggle', 'View details');
      toggle.type = 'button';
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-controls', 'swishcb-review-details');
      header.appendChild(toggle);
      content.prepend(header);
      if (!root.querySelector('.swishcb__review-details')) {
        const details = make('section', 'swishcb__review-details');
        details.id = 'swishcb-review-details';
        details.setAttribute('role', 'region');
        details.setAttribute('aria-label', 'Order Summary');
        details.tabIndex = -1;
        header.after(details);
      }
      const purchase = make('div', 'swishcb__purchase');
      content.appendChild(purchase);
      const notice = make('p', 'swishcb__checkout-notice');
      notice.dataset.swishcbCheckoutNotice = '';
      notice.setAttribute('role', 'status');
      notice.hidden = true;
      content.appendChild(notice);
    }
    const details = root.querySelector('.swishcb__review-details');
    const toggle = header.querySelector('.swishcb__review-toggle');
    if (toggle && details && !toggle.dataset.swishcbReviewBound) {
      toggle.dataset.swishcbReviewBound = 'true';
      toggle.addEventListener('click', () => {
        details.hidden = !details.hidden;
        toggle.setAttribute('aria-expanded', String(!details.hidden));
        text(toggle, details.hidden ? 'View details' : 'Hide details');
        if (!details.hidden) requestAnimationFrame(() => {
          details.scrollIntoView({block: 'start', behavior: 'instant'});
          details.focus({preventScroll: true});
        });
      });
    }
    const purchase = content.querySelector('.swishcb__purchase');
    content.querySelectorAll('[data-swishcb-v2-order-content]').forEach(node => {
      // Keep price and the original secure checkout form always visible.
      const action = node.querySelector('[data-swishcb-product-price], product-form, [id^="ProductSubmitButton-"]');
      if (action) append(purchase, node);
      else {
        const footer = details.querySelector(':scope > .swishcb__summary-purchase');
        const help = details.querySelector(':scope > [data-swishcb-panel="contact"]');
        details.insertBefore(node, footer || help);
      }
    });
    // Detailed specifications use the page scrollbar; only the purchase bar sticks.
    const navigation = root.querySelector('.swishcb__navigation-scroll');
    append(navigation, details);
    details.hidden = false;
    const tiles = details.querySelector('[data-swishcb-summary-tiles]');
    if (tiles) append(tiles, root.querySelector('[data-swishcb-panel="order-now"]'));
    append(tiles?.querySelector('.swishcb__order-series-controls'), root.querySelector('.swishcb__flooring-learn-more'));
    const helpPanel = root.querySelector('[data-swishcb-panel="contact"]');
    if (navigation && helpPanel && (helpPanel.parentElement !== navigation || details.nextElementSibling !== helpPanel)) details.after(helpPanel);
    const contact = content.querySelector('[data-swishcb-configurator-contact]');
    const priceState = content.querySelector('[data-swishcb-product-price]')?.dataset.swishcbPriceState;
    root.dataset.swishcbSidebarPriceState = priceState || 'loading';
    let actions = header.querySelector('.swishcb__review-actions');
    if (!actions) {
      actions = make('div', 'swishcb__review-actions');
      header.appendChild(actions);
      append(actions, header.querySelector('.swishcb__review-toggle'));
    }
    append(priceState === 'quote' ? purchase : actions, contact);
    text(contact, priceState === 'quote' ? 'Request pricing' : 'Contact us');
    updatePayment(root);
  }

  function mobilePreviewMenu(root, media, previewControls, toolbar, state) {
    if (root.matches('[data-swishcb-runtime-mode="order-editor"],.swishcb--hide-primary-builder-panels,.swishcb--three-court-card')) return null;
    const actions = toolbar.querySelector('.swishcb__toolbar-actions');
    if (!actions) return null;
    const originalChildren = Array.from(actions.children).filter(node => !node.matches('.swishcb__preview-menu-zoom-label'));
    let toggle = media.querySelector(':scope > [data-swishcb-preview-menu-toggle]');
    if (!toggle) {
      toggle = make('button', 'swishcb__preview-menu-toggle');
      toggle.type = 'button';
      toggle.hidden = true;
      toggle.dataset.swishcbPreviewMenuToggle = '';
      toggle.setAttribute('aria-controls', previewControls.id);
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', 'Show court preview controls');
      toggle.innerHTML = '<svg data-preview-menu-expand viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg><svg data-preview-menu-more viewBox="0 0 24 24" aria-hidden="true"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/></svg><svg data-preview-menu-close viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18"/></svg>';
      // Siblings of the live viewports cannot trigger their orbit handlers.
      media.appendChild(toggle);
    }
    let zoomLabel = actions.querySelector('.swishcb__preview-menu-zoom-label');
    if (!zoomLabel) {
      zoomLabel = make('span', 'swishcb__preview-menu-zoom-label', 'Zoom');
      zoomLabel.setAttribute('aria-hidden', 'true');
      zoomLabel.hidden = true;
      actions.appendChild(zoomLabel);
    }
    let active = false, open = false, reservedHeight = 0, style = 'panel';
    const move = node => { if (node) actions.appendChild(node); };
    const orderForMenu = () => {
      ['view-3d','view-2d'].forEach(action => move(actions.querySelector(`[data-action="${action}"]`)));
      move(actions.querySelector('[data-zoom-step="out"]'));
      move(zoomLabel);
      move(actions.querySelector('[data-zoom-step="in"]'));
      ['snapshot','share'].forEach(action => move(actions.querySelector(`[data-action="${action}"]`)));
      move(actions.querySelector('.swishcb__action--autorotate'));
      move(actions.querySelector('.swishcb__inline-group[aria-label="Rotation"]'));
      ['undo','redo'].forEach(action => move(actions.querySelector(`[data-action="${action}"]`)));
    };
    function setOpen(next, returnFocus = false) {
      open = active && !!next;
      root.toggleAttribute('data-swishcb-preview-menu-open', open);
      previewControls.hidden = active && !open;
      previewControls.inert = active && !open;
      if (active) previewControls.setAttribute('aria-hidden', String(!open));
      else previewControls.removeAttribute('aria-hidden');
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute('aria-label', open ? 'Hide court preview controls' : 'Show court preview controls');
      // The control row expands below the court; it never resizes the canvas.
      if (returnFocus && active) toggle.focus({preventScroll: true});
    }
    toggle.addEventListener('click', () => setOpen(!open), {signal: state.lifecycle.signal});
    const outside = event => {
      if (!active || !open || toggle.contains(event.target) || previewControls.contains(event.target)) return;
      // Builder updates may return focus to the main page after a toggle.
      // Only actual outside controls indicate keyboard navigation away.
      if (event.type === 'focusin' && !event.target.matches?.('a[href],button,input,select,textarea,[tabindex]:not([tabindex="-1"])')) return;
      // Native Save/Share dialogs retain their own focus and dismissal owner.
      if (event.target.closest?.('dialog,[role="dialog"]')) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', outside, {capture: true, passive: true, signal: state.lifecycle.signal});
    document.addEventListener('focusin', outside, {signal: state.lifecycle.signal});
    document.addEventListener('keydown', event => {
      if (!active || !open || event.key !== 'Escape' || event.target.closest?.('dialog,[role="dialog"]')) return;
      event.preventDefault();
      setOpen(false, true);
    }, {signal: state.lifecycle.signal});
    return {
      get active() { return active; },
      get reservedHeight() { return reservedHeight; },
      get minimumCanvasHeight() { return active && style === 'panel' ? 66 : 56; },
      get keyboardInset() { return active && style === 'panel' ? 22 : 0; },
      close: () => setOpen(false),
      update(enabled, focusedBefore = document.activeElement) {
        const focusedAction = previewControls.contains(focusedBefore) ? focusedBefore : null;
        const nextStyle = root.dataset.swishcbMobileNavigationStyle === 'overlay' ? 'overlay' : 'panel';
        if (enabled && (!active || style !== nextStyle)) {
          // Measure the original horizontal row before applying either style.
          if (!active) reservedHeight = Math.max(0, previewControls.getBoundingClientRect().height);
          active = true;
          style = nextStyle;
          if (style === 'overlay') orderForMenu();
          else originalChildren.forEach(move);
          zoomLabel.hidden = style !== 'overlay';
          root.dataset.swishcbPreviewMenu = 'true';
          root.dataset.swishcbPreviewMenuStyle = style;
          toggle.hidden = false;
          setOpen(false, !!focusedAction);
        } else if (!enabled && active) {
          const hadFocus = focusedBefore === toggle;
          active = false;
          setOpen(false);
          originalChildren.forEach(move);
          zoomLabel.hidden = true;
          toggle.hidden = true;
          root.removeAttribute('data-swishcb-preview-menu');
          root.removeAttribute('data-swishcb-preview-menu-style');
          root.removeAttribute('data-swishcb-preview-menu-compact');
          if (hadFocus) toolbar.querySelector('[aria-pressed="true"],button:not([disabled])')?.focus({preventScroll: true});
          else focusedAction?.focus({preventScroll: true});
        }
        if (active) {
          const canvas = media.querySelector('.swishcb__viewport:not(.swishcb__viewport--hidden)');
          root.toggleAttribute('data-swishcb-preview-menu-compact', !!canvas && canvas.getBoundingClientRect().height < 160);
        }
      }
    };
  }

  function bottomBar(root, checkout, state) {
    const product = root.querySelector(':scope > .product');
    const toolbar = root.querySelector('.swishcb__toolbar');
    const information = product?.querySelector('.product__info-wrapper');
    const media = product?.querySelector('.product__media-wrapper');
    if (!product || !toolbar || !checkout || !information || !media) return;
    let bar = product.querySelector(':scope > .swishcb__bottom-bar');
    if (!bar) {
      bar = make('div', 'swishcb__bottom-bar');
      bar.id = 'swishcb-bottom-bar';
      bar.append(make('div', 'swishcb__dock-preview'), make('div', 'swishcb__dock-order'));
      const previewControls = bar.querySelector('.swishcb__dock-preview');
      previewControls.id = 'swishcb-preview-controls';
      product.appendChild(bar);
    }
    const previewControls = root.querySelector('#swishcb-preview-controls');
    if (!previewControls) return;
    // Liquid supplies the dock in the native sidebar. Its observers and
    // responsive controls still bind once, independently of node creation.
    if (!state.scheduleDock) {
      const stacked = matchMedia('(max-width: 1199px), (max-width: 1366px) and (any-pointer: coarse)');
      const setSize = (node, name, value) => {
        if (node.style.getPropertyValue(name) !== value) node.style.setProperty(name, value);
      };
      const measure = () => {
        state.dockFrame = 0;
        // Keep the live toolbar below the preview on phones/tablets. Moving the
        // existing node preserves every action, and restores desktop tab order.
        const focusedBefore = document.activeElement;
        const destination = stacked.matches ? media : bar;
        if (previewControls.parentElement !== destination) {
          const before = stacked.matches ? null : bar.firstElementChild;
          if (destination.moveBefore) destination.moveBefore(previewControls, before);
          else destination.insertBefore(previewControls, before);
        }
        state.previewMenu?.update(stacked.matches, focusedBefore);
        const style = getComputedStyle(information);
        setSize(bar, '--swishcb-order-padding-start', style.paddingInlineStart);
        setSize(bar, '--swishcb-order-padding-end', style.paddingInlineEnd);
        // The padded desktop preview column extends to the viewport edge.
        // Measure its bleed from the unchanged builder grid to avoid feedback.
        const builderBox = root.getBoundingClientRect();
        const content = information.querySelector('.product__info-container') || information;
        const contentLeft = content.getBoundingClientRect().left;
        setSize(root, '--swishcb-preview-bleed-left', `${Math.max(0, builderBox.left)}px`);
        setSize(root, '--swishcb-preview-desktop-width', `${Math.max(1, contentLeft)}px`);
        if (!root.hasAttribute('data-swishcb-keyboard-open')) {
          setSize(root, '--swishcb-preview-mobile-width', `${document.documentElement.clientWidth}px`);
        }
        setSize(root, '--swishcb-bottom-bar-height', `${Math.ceil(bar.getBoundingClientRect().height)}px`);
        const toolbarHeight = state.previewMenu?.active ? state.previewMenu.reservedHeight : previewControls.getBoundingClientRect().height;
        setSize(root, '--swishcb-preview-toolbar-height', `${Math.ceil(toolbarHeight)}px`);
        setSize(root, '--swishcb-pinned-preview-height', stacked.matches ? `${Math.ceil(media.getBoundingClientRect().height)}px` : '0px');
      };
      state.scheduleDock = () => {
        if (!state.dockFrame) state.dockFrame = requestAnimationFrame(measure);
      };
      state.dockObserver = new ResizeObserver(state.scheduleDock);
      state.dockObserver.observe(information);
      state.dockObserver.observe(bar);
      state.dockObserver.observe(media);
      state.dockObserver.observe(previewControls);
      stacked.addEventListener('change', state.scheduleDock, {signal: state.lifecycle.signal});
      window.addEventListener('resize', state.scheduleDock, {signal: state.lifecycle.signal});
    }
    // Move the original nodes so preview actions and the checkout form keep their handlers.
    append(root.querySelector('#swishcb-preview-controls'), toolbar);
    if (!state.previewMenu) state.previewMenu = mobilePreviewMenu(root, media, root.querySelector('#swishcb-preview-controls'), toolbar, state);
    append(bar.querySelector('.swishcb__dock-order'), checkout);
    state.scheduleDock();
  }

  function sync(root) {
    if (!root?.matches(selector)) return;
    const controls = root.querySelector('.swishcb__controls');
    const settings = controls?.querySelector('[data-swishcb-settings-group]');
    if (!settings) return;
    const get = key => controls.querySelector(`[data-swishcb-panel="${key}"]`);
    const checkout = root.querySelector('[data-swishcb-panel="checkout"]');
    let state = states.get(root);
    if (state?.syncing) return;
    if (!state) {
      state = {syncing: false};
      states.set(root, state);
      root.dataset.swishcbSidebar = 'true';
      controls.id = 'swishcb-sidebar-controls';
      const lifecycle = new AbortController();
      state.lifecycle = lifecycle;
      controls.addEventListener('keydown', event => {
        const dropdown = event.target.closest('[data-swishcb-checkbox-dropdown][open]');
        if (event.key !== 'Escape' || !dropdown) return;
        event.preventDefault();
        event.stopPropagation();
        dropdown.open = false;
        dropdown.querySelector('summary')?.focus({preventScroll: true});
      }, {signal: lifecycle.signal});
      document.addEventListener('click', event => {
        root.querySelectorAll('[data-swishcb-checkbox-dropdown][open]').forEach(dropdown => {
          if (!dropdown.contains(event.target)) dropdown.open = false;
        });
      }, {signal: lifecycle.signal});
      document.addEventListener('shopify:section:unload', event => {
        if (event.target.contains(root)) {
          state.dockObserver?.disconnect();
          state.summaryPurchaseObserver?.disconnect();
          cancelAnimationFrame(state.dockFrame);
          state.editorFocus?.destroy();
          state.paletteObserver?.disconnect();
          state.paletteContentObserver?.disconnect();
          cancelAnimationFrame(state.paletteFrame);
          state.paint?.observer.disconnect();
          cancelAnimationFrame(state.paint?.frame);
          state.paint = null;
          cancelAnimationFrame(state.courtFrame);
          state.courtSnapshot = null;
          state.logoCourtBitmap = null;
          lifecycle.abort();
        }
      }, {signal: lifecycle.signal});
      controls.addEventListener('keydown', paletteKeydown, {signal: lifecycle.signal});
      controls.addEventListener('focusin', event => {
        const row = event.target.closest('[data-swishcb-palette-inline]');
        state.paletteFocus = row && event.target.matches('button') ? {row, button: event.target, hex: event.target.dataset.hex} : null;
      }, {signal: lifecycle.signal});
      controls.addEventListener('focusout', event => {
        if (event.target.matches('#swishcb-contact-width,#swishcb-contact-length,[data-swishcb-contact-unit-select]')) {
          requestAnimationFrame(() => contactDimensions(root));
        }
      }, {signal: lifecycle.signal});
      state.paletteContentObserver = new MutationObserver(() => {
        if (state.paletteFrame) return;
        state.paletteFrame = requestAnimationFrame(() => {
          state.paletteFrame = 0;
          palettes(root);
          const focused = state.paletteFocus;
          if (focused && !focused.button.isConnected && document.activeElement === document.body && focused.row.isConnected) {
            const replacement = focused.hex ? focused.row.querySelector(`[data-hex="${focused.hex}"]`) : focused.row.querySelector('[data-color-customize]');
            replacement?.focus({preventScroll: true});
            palettes(root);
          }
        });
      });
      const colors = get('colors');
      if (colors) state.paletteContentObserver.observe(colors, {childList: true, subtree: true});
      controls.addEventListener('change', event => {
        const input = event.target;
        if (input.matches('[data-extra-toggle]') && input.checked) {
          const card = input.closest('[data-extra-card]');
          const options = card?.querySelector('[data-extra-panel-toggle]');
          if (options?.getAttribute('aria-expanded') !== 'true') options?.click();
        }
        requestAnimationFrame(() => sync(root));
      }, {signal: lifecycle.signal});
      const updateOverhang = event => {
        if (!event.target.matches('[data-hoop-distance]')) return;
        const host = event.target.closest('[data-swishcb-embedded-hoop-details]');
        if (host) host.dataset.swishcbOverhangSide = event.target.dataset.hoopDistance;
        requestAnimationFrame(() => overhang(root));
      };
      controls.addEventListener('input', updateOverhang, {signal: lifecycle.signal});
      controls.addEventListener('focusin', updateOverhang, {signal: lifecycle.signal});
      state.editorFocus = mobileEditorFocus(root, controls, state);
      // Summary edit links use the same source controls, including hidden panels.
      controls.addEventListener('click', event => {
        if (!event.target.closest('.swishcb__review-details')) return;
        requestAnimationFrame(() => {
          const active = document.activeElement;
          if (controls.contains(active)) openPanel(active.closest('details[data-swishcb-panel]'));
        });
      }, {signal: lifecycle.signal});
    }
    state.syncing = true;
    try {
      let scroll = controls.querySelector(':scope > .swishcb__navigation-scroll');
      if (!scroll) {
        scroll = make('div', 'swishcb__navigation-scroll');
        scroll.setAttribute('role', 'region');
        scroll.setAttribute('aria-label', 'Customize your court');
        controls.prepend(scroll);
      }
      append(scroll, settings);
      const surfacePanel = get('order-now');
      if (!surfacePanel?.closest('[data-swishcb-summary-tiles]')) append(scroll, surfacePanel);
      updatePayment(root);
      bottomBar(root, checkout, state);
      section(get('main-info'), 'Court Settings', 'Set your court’s width and length.');
      section(get('colors'), 'Choose Your Colors', 'Select any colors at no extra cost.');
      artworkControls(root);
      section(get('order-now'), 'Court Surface', 'Select the flooring for your court.');
      contactSection(get('contact'));
      sportsRows(root);
      sizeFields(root, get('main-info'));
      overhang(root);
      palettes(root);
      surface(root);
      review(root, checkout);
      contactDimensions(root);
      navigationLabels(root);
      checkboxDropdowns(root);
      closeActions(root);
    } finally { state.syncing = false; }
  }
  window.SwishCBSidebar = {sync, openPanel, refreshSports, updateCourtDiagram, captureLogoCourtDiagram, updateLogoIllustration, refreshReview: root => {
    if (root?.matches(selector)) review(root, root.querySelector('[data-swishcb-panel="checkout"]'));
  }};
  const init = () => document.querySelectorAll(selector).forEach(sync);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once:true});
  else init();
  document.addEventListener('shopify:section:load', init);
})();
